/**
 * THE FEED CONFIGURATOR (One Design step 4, D-313). Layout A with D as the safety net (Henry, 2026-10-02; mockups
 * https://claude.ai/artifact/JjQY4E8wiDPDCaykhLNpxc and https://claude.ai/artifact/7axRDd4PZhSWs3FHomtfnk):
 *   A · metal as swatches; every other axis as a chip grid of the WHOLE offered set; a value with no combination beside
 *       the current picks is greyed and hatched but still shown; one note per axis says what is and isn't made.
 *   D · when a change leaves a pick that no longer exists, move to the nearest one that does and say so.
 * The page is a projection of the intake engine: everything comes from CATALOG's /feed/offered (shape below), never
 * from product.variants, and an optional field the route does not send yet (family, range, facts, lead) draws nothing.
 *
 * GET  offeredUrl?product=<id> → { design, productId, rule, axes: [{ name, values: [{ value }] }], combos: [{ options,
 *        priceCents, variantId|null, sellable: 'live'|'mint'|'draft'|'staffed', leadDays?, facts?, imageUrl?, images?, sku? }], cap,
 *        family?, range?, facts?, details?, siblings?: [{ axis, value, handle, current }] }
 *      an axis may carry parts: ['Detail', 'Width', 'Weight'] with values[].parts aligned (THEME-CONTRACT §I.3).
 *      finishes?: [{ code, name, priceCents, variantId, swatchUrl? }] with combos[].finishCodes / finishImages? (D-321, §I.5).
 * POST makeUrl { productId, options } → { kind: 'variant', variantId, priceCents } | { kind: 'draft', invoiceUrl,
 *        priceCents, sentence } · 422 { error } (shown as is) · anything else: call us.
 */
(() => {
  const root = document.querySelector('[data-djh-feed]');
  const cfgEl = document.querySelector('[data-djh-feed-config]');
  if (!root || !cfgEl) return;
  const cfg = JSON.parse(cfgEl.textContent);
  const m = cfg.messages;
  const form = root.closest('form');
  const $ = (sel) => root.querySelector(sel);
  const shelf = $('[data-djh-feed-shelf]');
  const picker = $('[data-djh-feed-picker]');
  const axesBox = $('[data-djh-feed-axes]');
  const draftNote = $('[data-djh-feed-draft]');
  const staffedNote = $('[data-djh-feed-staffed]');
  const status = $('[data-djh-feed-status]');
  // Below the buttons, where every product page says when it arrives (product.liquid renders it on the feed template).
  const leadLine = document.querySelector('[data-djh-feed-lead]');
  // Product Details' Ring / Diamond information, built from the pick (never the arriving variant's options).
  const detailsBox = document.querySelector('[data-djh-feed-details]');
  // The site's own "SKU:" line under the price. It follows the pick; empty until a whole pick has a SKU.
  const skuLine = document.querySelector('[data-djh-sku]');
  const skuValue = skuLine && skuLine.querySelector('[data-djh-sku-value]');
  const button = form && form.querySelector('[data-djh-add-to-cart]');
  const priceDisplay = document.querySelector('[data-djh-price-display]');
  const dynamicCheckout = form && form.querySelector('[data-djh-dynamic-checkout]');
  const mainImage = document.querySelector('[data-djh-main-image]');
  // The thumbnail row under the photo (product.liquid's per-variant gallery). Rebuilt from the pick's own angle renders
  // when CATALOG sends combos[].images; left as it is when it doesn't. Its click handler is delegated, so new thumbs work.
  const thumbsBox = document.querySelector('[data-djh-dynamic-thumbs]');
  if (mainImage) mainImage.addEventListener('load', () => { mainImage.style.transform = fitCss(mainImage.currentSrc || mainImage.src); });
  let thumbsFor = null;
  const shelfImage = mainImage && { src: mainImage.getAttribute('src'), srcset: mainImage.getAttribute('srcset') };

  const el = (tag, attrs, ...kids) => {
    const n = document.createElement(tag);
    Object.entries(attrs || {}).forEach(([k, v]) => { if (v != null && v !== false) n.setAttribute(k, v === true ? '' : v); });
    kids.flat().forEach((k) => { if (k != null) n.append(k); });
    return n;
  };
  const money = (cents) => (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  const fill = (s, map) => Object.entries(map).reduce((t, [k, v]) => t.split(k).join(v), s);
  // The store's values stay the values (H4 keeps "Mm" until CATALOG re-mints); only what is SHOWN reads "mm".
  // "6 X 4 Mm" → "6 × 4 mm": the store's values stay as they are; only what is shown reads as a jeweler writes it.
  const shown = (v) => String(v).replace(/\bMm\b/g, 'mm').replace(/(\d) [Xx] (\d)/g, '$1 × $2')
    // Stuller names the weights "Ultra-Lightweight … Heavyweight" and "Standard Weight"; the shop says it in one word each.
    .replace(/\bUltra-Lightweight\b/, 'Ultra-Light').replace(/\bLightweight\b/, 'Light').replace(/\bStandard Weight\b/, 'Standard').replace(/\bHeavyweight\b/, 'Heavy');
  // The FIRST number in a value: "Asscher · 2 X 2 mm" is 2, not the 22 that stripping every non-digit made of it.
  const num = (v) => { const n = String(v).match(/\d+(?:\.\d+)?/); return n ? parseFloat(n[0]) : NaN; };
  // Birthstone chips (family and mother's rings): a stone axis reads as a color dot, the stone and its month, the way a
  // buyer thinks of it. The months follow the common modern list and are shop copy for Henry to confirm. It applies only
  // when EVERY value on the axis is a stone we can name: a single unknown value leaves the whole axis as plain chips.
  const GEMS = [
    ['mozambique garnet', 'January', '#7a1f2b'], ['garnet', 'January', '#7a1f2b'], ['amethyst', 'February', '#9b5fd6'],
    ['aquamarine', 'March', '#aee8ef'], ['white sapphire', 'April', '#f2f3f6'], ['diamond', 'April', '#f2f3f6'],
    ['emerald', 'May', '#1f9a58'], ['alexandrite', 'June', 'linear-gradient(135deg, #2a9d8f 50%, #7b5fa8 50%)'], ['ruby', 'July', '#d4142f'], ['peridot', 'August', '#9ac13c'],
    ['blue sapphire', 'September', '#1f4fb2'], ['pink tourmaline', 'October', '#ee80aa'], ['citrine', 'November', '#f0a42a'],
    ['blue zircon', 'December', '#1e9bd7'],
    ['london blue topaz', 'December', '#2f78b5'], ['blue topaz', 'December', '#4aa3d8'],
  ];
  // A fancy-colored diamond is not April's white one: it gets no dot rather than the wrong one. FANCY_WORDS is mirrored in
  // snippets/djh-gem-dot.liquid; scripts/qa-gem-table.py fails when the two differ.
  const FANCY_WORDS = ['black', 'champagne', 'yellow', 'brown', 'cognac', 'fancy', 'blue', 'pink', 'green', 'orange'];
  const gemOf = (v) => { const d = String(v).toLowerCase(); if (d.includes('diamond') && FANCY_WORDS.some((w) => d.includes(w))) return null; return GEMS.find((g) => d.includes(g[0])) || null; };
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  // Chips run January to December so a shopper finds their birth month without reading every name; "Mozambique" is dropped from
  // the chip (the full name stays in the title and in the cart line).
  const gemRank = (v) => MONTHS.indexOf(gemOf(v)[1]);
  // The origin is said once, by the Natural | Lab-Grown switch above, so the stone's own label drops it ("Natural Ruby" reads "Ruby").
  const gemLabel = (v) => shown(v).replace(/^(?:natural|lab[- ]?grown)\s+/i, '').replace(/^Mozambique\s+/i, '');
  const isGemAxis = (axis) => /stone/i.test(axis.name) && !/shape|size|count/i.test(axis.name) && axis.values.length > 0 && axis.values.every(gemOf);
  const isMetal = (name) => /metal/i.test(name);
  const SHOP_ORDER = ['14k yellow', '18k yellow', '10k yellow', '14k white', '18k white', '10k white', '14k rose', '18k rose', '10k rose', 'sterling', 'platinum'];
  const metalRank = (v) => { const d = v.toLowerCase(); const i = SHOP_ORDER.findIndex((k) => d.includes(k)); return i < 0 ? 99 : i; };
  const tone = (v) => { const d = v.toLowerCase(); return d.includes('rose') ? 'rose' : d.includes('yellow') ? 'yellow' : d.includes('platinum') ? 'platinum' : d.includes('palladium') ? 'palladium' : d.includes('sterling') ? 'sterling' : d.includes('white') ? 'white' : 'other'; };

  // A 72 px thumbnail needs Stuller's 300 px render ($standard$, ~11 KB), not the 640 px one the main photo shows
  // ($xlarge$, ~38 KB): a new pick's five thumbnails load ~3.5x lighter. The full size stays in data-full-src.
  const thumbSrc = (url) => (/stullercloud\.com/.test(url) ? String(url).replace('$xlarge$', () => '$standard$') : url);
  let data = null;
  let axes = [];          // [{ name, metal, values: [string] }] in the route's order, values sorted for people
  let chosen = [];        // one value per axis, or null
  let byKey = new Map();  // "a|b|c" → combo
  let idInput = null;
  let startPicks = null;  // the Stone rows a count-driven ring opens on (mixed birthstones, one for each person)
  let comboInput = null;
  let message = null;     // the D note, shown once on the axis it concerns
  const key = (opts) => opts.join('|');

  // Does any offered combination match these picks (null = any)? Answered from an index of every combination projected
  // onto every subset of the axes, built once: the Half Round Band has 13,138 combinations and the page asks this for
  // every button on every paint, so a scan per question froze a throttled phone.
  let seen = new Set();
  function index() {
    seen = new Set();
    data.combos.forEach((c) => {
      const n = c.options.length;
      for (let mask = 1; mask < (1 << n); mask++) seen.add(`${mask}:${c.options.filter((_, i) => mask & (1 << i)).join('|')}`);
    });
  }
  function exists(picks) {
    let mask = 0;
    const vals = [];
    picks.forEach((v, i) => { if (v != null) { mask |= 1 << i; vals.push(v); } });
    return mask ? seen.has(`${mask}:${vals.join('|')}`) : data.combos.length > 0;
  }

  // CATALOG's v2 file (the prebuilt picker file): columns, not one object per combination. Turned into the combinations
  // the rest of the page reads; what a single pick needs beyond price and state (SKU, facts, photo, finishes) is asked of
  // /feed/pick once it is chosen. State: 0 call us · 1 made to order · 2 priced draft (at the cap) · 3 live variant.
  const STATE = ['staffed', 'mint', 'draft', 'live'];
  function fromFile(f) {
    const g = f.grid;
    const combos = g.p.map((cents, r) => {
      const variantId = g.v && g.v[r] != null ? String(g.v[r]) : null;
      const sellable = cents == null ? 'staffed' : STATE[g.s[r]] || 'staffed';
      // The preview photo from the file's map (imgKey: the axes that change the photo; img: "i0|i1" → url), so a partial
      // pick looks like its metal and stone before a size is chosen. A whole pick's own photo replaces it from /feed/pick.
      const photoKey = Array.isArray(f.imgKey) ? f.imgKey.map((n) => g.a[n][r]).join('|') : null;
      const imageUrl = f.img && photoKey ? f.img[photoKey] || null : null;
      // The angle set for the same key (angles: {"i0|i1": [url…]}), so the thumbnails show on open and follow a partial
      // pick without waiting on /feed/pick; a whole pick's own set from /feed/pick replaces it.
      const images = f.angles && photoKey && Array.isArray(f.angles[photoKey]) ? f.angles[photoKey] : undefined;
      return { options: f.axes.map((a, n) => a.values[g.a[n][r]].value), priceCents: cents, variantId, imageUrl, images,
        sellable: sellable === 'live' && !variantId ? 'mint' : sellable, leadDays: g.d ? g.d[r] : null, needsDetail: true };
    });
    return { ...f, combos };
  }
  const asking = new Set();
  // The doors (/feed/pick, /feed/make) and the cart property speak Shopify's three options. A ring with a Count axis and Stone rows
  // (71816) folds them the way its Shopify option does: "4 × Citrine" in the place of Count and Stone, the origin word dropped.
  function doorOptions(opts) {
    const c = axes.findIndex((a) => a.count);
    const s = axes.findIndex((a) => a.positions === 'count');
    if (c < 0 || s < 0) return opts;
    const out = [];
    opts.forEach((v, i) => {
      if (i === c) return;
      out.push(i === s ? `${opts[c]} \u00d7 ${String(v).replace(/^(?:natural|lab[- ]?grown)\s+/i, '')}` : v);
    });
    return out;
  }
  const doorKey = (opts) => key(doorOptions(opts));
  async function loadPick(combo) {
    const k = doorKey(combo.options);
    // A ring of stone sets has no supplier SKU for the pick door to look up (CATALOG, 3 Nov): its facts and photos come from the offered file.
    if (stoneApi || !combo.needsDetail || asking.has(k) || !cfg.pickUrl) return;
    asking.add(k);
    try {
      const res = await fetch(`${cfg.pickUrl}?product=${encodeURIComponent(cfg.productId)}&o=${encodeURIComponent(k)}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!res.ok) return;
      const d = await res.json();
      if (d && d.ok === false) {
        // Slow or erroring engine: one more ask after retryAfter; the pick still sells on its price and state meanwhile.
        if (!combo.retried) {
          combo.retried = true;
          const after = Math.min(Math.max(Number(d.retryAfter) || 1, 0.5), 3) * 1000;
          setTimeout(() => { loadPick(combo); }, after);
        }
        return;
      }
      combo.needsDetail = false;
      ['sku', 'facts', 'imageUrl', 'finishCodes', 'finishImages'].forEach((f) => { if (d[f] != null) combo[f] = d[f]; });
      // The whole pick's own views win over the file's per-metal-and-stone set; a pick that brings only its photo shows
      // that photo rather than the partial set.
      if (Array.isArray(d.angles) || Array.isArray(d.images)) combo.images = d.angles || d.images;
      else if (d.imageUrl) combo.images = undefined;
      if (Number.isFinite(d.leadDays)) combo.leadDays = d.leadDays;
      if (!combo.variantId && d.variantId) combo.variantId = String(d.variantId);
      if (byKey.get(key(chosen)) === combo) paint();
    } catch (_e) { /* the pick still sells on its price and state; its extra rows simply don't draw */ } finally {
      asking.delete(k);
    }
  }
  const withValue = (i, v) => chosen.map((c, j) => (j === i ? v : c));
  // A value is "made" beside the picks on the axes BEFORE it (metal, then width…), the mockup's reading order.
  const made = (i, v) => exists(withValue(i, v).map((c, j) => (j <= i ? c : null)));
  // What is SHADED reads every other pick, earlier and later (Henry, 2026-10-02: a metal not made in the chosen stone
  // and size is hatched too). A shaded value stays tappable; D still moves the later picks to the nearest made one.
  const available = (i, v) => exists(withValue(i, v));
  let touched = false;    // the customer has picked something; until then the page keeps the photo it opened with
  let picked = false;     // the customer has changed a choice (the opening ring's own photo stays until then)
  // The pick the page opened on and the price it arrived with (the card's number, server-rendered): the header keeps that price while the
  // choices are the opening ones, and shows it again if the shopper comes back to them. Read once, on the first paint.
  let openingAt = null; let openingPrice = null; let openingSeed = null;
  let pickTimer = null;
  // The finish add-on (D-321): Polished is included; any other finish the pick's own Stuller item lists is a priced line of
  // its own in the cart (finishes[].variantId), at the route's price. null = Polished.
  let finish = null;
  let finishNote = '';
  const finishOf = (code) => (Array.isArray(data && data.finishes) ? data.finishes : []).find((f) => f.code === code) || null;
  // The finishes every combination matching the picks allows: a finish shows only when the item it would land on lists it.
  function finishCodes() {
    const matching = data.combos.filter((c) => chosen.every((v, i) => v == null || c.options[i] === v));
    if (!matching.length) return [];
    const lists = matching.map((c) => (Array.isArray(c.finishCodes) ? c.finishCodes : []));
    return lists[0].filter((code) => finishOf(code) && lists.every((l) => l.includes(code)));
  }

  function nearest(i, from) {
    const ok = axes[i].values.filter((v) => made(i, v));
    if (!ok.length) return null;
    if (axes[i].metal) return ok[0];
    // A parted value (Detail · Width · Weight) keeps as many of the customer's parts as it can, then the closest number
    // in the parts that differ: Milgrain · 8 mm · Standard moves to Milgrain · 7.5 mm · Standard, not to Plain · 8 mm.
    if (axes[i].parted && from != null && axes[i].partOf.has(from)) {
      const want = axes[i].partOf.get(from);
      const off = (v) => axes[i].partOf.get(v).reduce((sum, q, k) => {
        if (q === want[k]) return sum;
        const d = Math.abs(num(q) - num(want[k]));
        return sum + 1000 + (Number.isFinite(d) ? d : 0);
      }, 0);
      return ok.slice().sort((a, b) => off(a) - off(b))[0];
    }
    return ok.slice().sort((a, b) => Math.abs(num(a) - num(from)) - Math.abs(num(b) - num(from)))[0];
  }

  // D · after a change on axis `changed`, any later pick that no longer exists moves to the nearest one, with a note.
  function settle(changed, tapped) {
    for (let i = changed + 1; i < axes.length; i++) {
      if (chosen[i] == null || made(i, chosen[i])) continue;
      const was = chosen[i];
      const near = nearest(i, was);
      const prev = shown(chosen[i - 1]);
      message = { axis: i, text: fill(m.moved, { N_PICK: prev, N_AXIS: axes[i].name.toLowerCase(), N_WAS: shown(was), N_NEAR: near == null ? '' : shown(near) }) };
      chosen[i] = near;
    }
    if (tapped != null && !made(tapped, chosen[tapped])) {
      const prev = shown(chosen[tapped - 1] == null ? '' : chosen[tapped - 1]);
      const near = nearest(tapped, chosen[tapped]);
      message = { axis: tapped, text: fill(m.closest, { N_PICK: prev, N_AXIS: axes[tapped].name.toLowerCase(), N_WAS: shown(chosen[tapped]), N_NEAR: near == null ? '' : shown(near) }) };
      chosen[tapped] = null;
    }
  }

  function noteFor(i) {
    if (message && message.axis === i) return { text: message.text, warn: true };
    // Only what shading cannot say (Henry approved the mockup without the "isn't made in…" and "pick … first" lines):
    // where a pick was moved, and the size span of the stone chosen.
    const before = chosen.slice(0, i);
    // "X comes in …" needs an earlier pick to be about, and metals already show as swatches (a one-option pendant said
    // " comes in metals 14K Yellow Gold to Platinum.", proof 3's shape, 2026-10-02).
    if (i === 0 || axes[i].metal || before.some((v) => v == null)) return { text: '', warn: false };
    const ok = axes[i].values.filter((v) => made(i, v));
    const lead = i > 0 ? shown(chosen[i - 1]) : '';
    const plural = (s) => (/s$/.test(s) ? s : `${s}s`);
    if (i === axes.length - 1 && ok.length === 1) return { text: fill(m.comesInOne, { N_PICK: lead, N_AXIS: axes[i].name.toLowerCase(), N_VALUE: shown(ok[0]) }), warn: false };
    // A span reads "5 to 7" only when nothing between is missing; with gaps (5, 6 and 7 but not 5.5 or 6.5) it names them.
    const span = axes[i].values.slice(axes[i].values.indexOf(ok[0]), axes[i].values.indexOf(ok[ok.length - 1]) + 1);
    if (i === axes.length - 1 && ok.length && span.length !== ok.length) {
      // Runs of neighbouring values read as one span: "1.5, 4 to 15.5, 16.5 and 17.5 to 20", not thirty-two numbers.
      const at = (v) => axes[i].values.indexOf(v);
      const runs = [];
      ok.forEach((v) => { const last = runs[runs.length - 1]; if (last && at(v) === at(last[last.length - 1]) + 1) last.push(v); else runs.push([v]); });
      const words = runs.map((r) => (r.length > 2 ? fill(m.span, { N_FROM: shown(r[0]), N_TO: shown(r[r.length - 1]) }) : r.map(shown).join(', ')));
      const list = words.length > 1 ? `${words.slice(0, -1).join(', ')} ${m.and} ${words[words.length - 1]}` : words[0];
      return { text: fill(m.comesInList, { N_PICK: lead, N_AXIS: plural(axes[i].name.toLowerCase()), N_LIST: list }), warn: false };
    }
    if (i === axes.length - 1 && ok.length) return { text: fill(m.comesIn, { N_PICK: lead, N_AXIS: plural(axes[i].name.toLowerCase()), N_FROM: shown(ok[0]), N_TO: shown(ok[ok.length - 1]) }), warn: false };
    return { text: '', warn: false };
  }

  // The picture of a ring of different stones: the store holds renders for the same stone all round only. A fresh mixed set shows the
  // file's "family idea" render when it has one; a changed mixed set shows the strip of the stones as chosen, left to right.
  // The picture of a MIXED set, drawn on request. After the shopper has settled on a mix the picker points an image at the file's renderUrl
  // (the shop's own /apps/djh door): it answers a picture or an error and keeps nothing waiting. The strip stays until the picture has loaded,
  // a quiet line says it is being drawn, and a slow or failed answer leaves the strip as it is. Nothing here touches the buttons, and a file
  // that carries no renderUrl (71175, 71087) never asks. Pictures of other metals are not asked for: the door allows 60 new pictures an hour.
  // settleMs is how long the shopper must stop before a picture is asked for. The door allows 60 NEW pictures an hour, so the wait is not zero:
  // 250 ms catches a tap-tap-tap through the list as one ask (it was 600). A quick failure (under retryWithinMs: the network or a gateway, not a
  // render, which takes seconds) is asked once more after retryAfterMs.
  const DRAW = { settleMs: 250, hintAfterMs: 350, hintForMs: 12000, giveUpMs: 15000, failedForMs: 60000, retryWithinMs: 1500, retryAfterMs: 400 };
  const drawn = new Map();   // key → url, for the visit: going back to a mix already drawn is instant
  const failed = new Map();  // key → when it last failed or gave up
  let drawKey = null; let drawTimer = null; let drawImg = null; let hintTimers = []; let hintEl = null;
  // The address of a mixed set in one metal: null while any stone has no canonical key or the metal is not chosen.
  function drawParams(metalValue) {
    const mAt = axes.findIndex((a) => a.metal);
    const sAt = axes.findIndex((a) => a.positions === 'count');
    if (!data.renderUrl || mAt < 0 || sAt < 0 || !metalValue) return null;
    return doorAddress(metalValue, stoneApi.picks(), sAt);
  }
  // The door's address for a set of stones in one metal, without a width: the hero asks it at the device's width, the gallery's angles add theirs.
  function doorAddress(metalValue, picks, sAt) {
    const keys = picks.map((v) => axes[sAt].keyOf.get(v) || null);
    if (keys.length < 1 || keys.some((k) => !k)) return null;
    const stones = keys.join('|');
    const base = `${data.renderUrl}?product=${encodeURIComponent(String(data.productId || cfg.productId))}&metal=${encodeURIComponent(metalValue)}&stones=${stones}`;
    return { key: `${metalValue}~${stones}`, metal: metalValue, base, url: `${base}&width=${galWide()}` };
  }
  function drawHint() {
    if (!hintEl) {
      hintEl = el('p', { class: 'djh-feed__draw-hint', role: 'status', hidden: true }, m.drawing || 'Drawing your ring', el('span', { class: 'djh-feed__draw-dots', 'aria-hidden': 'true' }, el('i'), el('i'), el('i')));
    }
    // On the photo, at its foot, so it never moves what is below it (it used to sit in the flow and push the page down 24 px each time it came and went).
    const box = (mainImage || document.querySelector('.djh-product__placeholder') || {}).parentElement;
    if (box && hintEl.parentElement !== box) box.append(hintEl);
    return hintEl;
  }
  const hideHint = () => { hintTimers.forEach(clearTimeout); hintTimers = []; if (hintEl) hintEl.hidden = true; };
  // A request nobody is waiting for any more: the image is let go, so a late answer lands on nothing.
  function cancelDraw() {
    clearTimeout(drawTimer);
    if (drawImg) { drawImg.onload = null; drawImg.onerror = null; drawImg.src = ''; drawImg = null; }
    drawKey = null;
    hideHint();
  }
  // Called from every paint with the mix the shopper is on now (null when the page is not on a changed mix).
  function wantDraw(prm) {
    if (!prm) { if (drawKey) cancelDraw(); return; }
    if (drawKey === prm.key) return;
    cancelDraw();
    drawKey = prm.key;
    if (drawn.has(prm.key)) return;
    if (failed.has(prm.key) && Date.now() - failed.get(prm.key) < DRAW.failedForMs) return;
    drawTimer = setTimeout(() => runDraw(prm), DRAW.settleMs);
  }
  function runDraw(prm) {
    const hint = drawHint();
    const t0 = Date.now(); let tries = 0; let done = false;
    const finish = (ok) => {
      if (done || drawKey !== prm.key) return;   // the shopper moved on: a stale answer is dropped
      done = true;
      if (drawImg) { drawImg.onload = null; drawImg.onerror = null; drawImg = null; }
      hideHint();
      if (ok) { drawn.set(prm.key, prm.url); paint(); } else { failed.set(prm.key, Date.now()); perfDone(prm.key, 'kept'); galPoke(); }
    };
    const attempt = () => {
      if (done || drawKey !== prm.key) return;
      tries += 1;
      const img = new Image();
      drawImg = img;
      // Decoded before it is swapped in, so the frame never stalls on the decode of a 1500 px picture.
      img.onload = () => { if (drawImg !== img) return; (img.decode ? img.decode() : Promise.resolve()).then(() => { fitOf(img, prm.url); finish(true); }, () => finish(true)); };
      img.onerror = () => {
        if (drawImg !== img) return;
        if (tries < 2 && Date.now() - t0 < DRAW.retryWithinMs) { img.onload = null; img.onerror = null; drawImg = null; hintTimers.push(setTimeout(attempt, DRAW.retryAfterMs)); return; }
        finish(false);
      };
      img.src = prm.url;
    };
    hintTimers = [
      // the soft white frame of a metal change says so at once; a held picture waits a moment first
      setTimeout(() => { if (drawKey === prm.key) hint.hidden = false; }, mainImage && mainImage.parentElement && mainImage.parentElement.classList.contains('djh-feed--hold') ? 0 : DRAW.hintAfterMs),
      setTimeout(() => { hint.hidden = true; }, DRAW.hintForMs),
      setTimeout(() => { if (!done && drawKey === prm.key) { if (drawImg) { drawImg.onload = null; drawImg.onerror = null; drawImg.src = ''; } finish(false); } }, DRAW.giveUpMs),
    ];
    attempt();
  }

  // THE GALLERY (D-385, CATALOG's door contract 2026-11-04): every angle Stuller draws, each one Stuller's own unmodified picture of the stones
  // as chosen. The row under the photo is the hero, then front, upright, edge-on (side) and the other three-quarter (mirror); it is there from the
  // first paint (plain squares until a picture arrives) and each thumbnail keeps its last good picture until the new one is in. After a change the
  // shopper has stopped on (300 ms) and once the hero is on screen the angles are asked one at a time, only for thumbnails in view, at 800 px; the
  // selected angle on a wide screen asks its 1200 px picture as the second request in flight. The next change lets every request go and drops late
  // answers. An angle the door cannot draw shows that angle at the opening stones, or the hero, labelled "Shown with sample stones" and stays. A
  // design the door answers 422 {sample} for (71087) shows the sample photos of the count through the photo door. A 429 stops the asking for that
  // mix: what is shown stays and nothing retries in a loop. Nothing here changes a picture, a price or a button.
  const GAL = { settleMs: 300, giveUpMs: 15000, thumbW: 800, most: 5 };
  const ANGLES = ['hero', 'front', 'upright', 'side', 'mirror'];
  // The ring is not the same size inside every picture: measured on QA, it fills 69 to 71% of the frame in the file's renders and 86 to 89% in the
  // door's, so every swap made it grow or shrink. A display scale on the frame (never an edit of the file) brings the door's pictures to the file's
  // framing; a picture that cannot be read keeps its own size.
  const FIT = { target: 0.71, probe: 120 };
  const fit = new Map();
  const absUrl = (u) => { try { return new URL(u, location.href).href; } catch (_e) { return String(u); } };
  function fitOf(img, url) {
    const k = absUrl(url);
    if (fit.has(k)) return fit.get(k);
    let sc = 1;
    try {
      const cv = document.createElement('canvas'); cv.width = FIT.probe; cv.height = FIT.probe;
      const cx = cv.getContext('2d', { willReadFrequently: true });
      cx.drawImage(img, 0, 0, FIT.probe, FIT.probe);
      const d = cx.getImageData(0, 0, FIT.probe, FIT.probe).data;
      let x0 = FIT.probe; let y0 = FIT.probe; let x1 = 0; let y1 = 0;
      for (let y = 0; y < FIT.probe; y += 1) for (let x = 0; x < FIT.probe; x += 1) {
        const i = (y * FIT.probe + x) * 4;
        if (d[i + 3] > 8 && (d[i] < 240 || d[i + 1] < 240 || d[i + 2] < 240)) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      const r = Math.max(x1 - x0, y1 - y0) / FIT.probe;
      if (r > FIT.target) sc = Math.max(0.5, FIT.target / r);
    } catch (_e) { sc = 1; }
    fit.set(k, sc);
    return sc;
  }
  const fitCss = (url, source) => { if (source && !fit.has(absUrl(url))) fitOf(source, url); const sc = fit.get(absUrl(url)); return sc && sc < 0.995 ? `scale(${sc.toFixed(3)})` : ''; };
  function galWide() { return window.matchMedia && window.matchMedia('(min-width: 861px)').matches ? 1200 : 800; }
  const gal = { key: null, run: 0, timer: null, asks: new Set(), big: null, stopKey: null, sel: 0, photos: null, items: [], els: [], view: null, label: null, touch: null, bigFor: null };
  const letGo = (img) => { if (img) { img.onload = null; img.onerror = null; img.src = ''; } };
  function galCount() { const cAt = axes.findIndex((a) => a.count); const n = cAt >= 0 ? parseInt(String(chosen[cAt]).replace(/^\D+/, ''), 10) : NaN; return Number.isFinite(n) ? n : 0; }
  // The mix the shopper is on, as the door addresses it; null where the door cannot draw it (one stone, a stone with no key, no door in the file).
  function galParams() {
    if (!data.renderUrl || !stoneApi || !thumbsBox) return null;
    return drawParams(chosen[axes.findIndex((a) => a.metal)]);
  }
  function galOpening() {
    const sAt = axes.findIndex((a) => a.positions === 'count');
    const mAt = axes.findIndex((a) => a.metal);
    const o = sAt >= 0 && mAt >= 0 && chosen[mAt] ? doorAddress(chosen[mAt], stoneApi.opening(), sAt) : null;
    return o && o.base;
  }
  const galHeroReady = (prm) => !stoneApi.mixed() || stoneApi.pristine() || drawn.has(prm.key) || failed.has(prm.key) || !!(cmp.shown && cmp.cv);
  function galReset(prm) {
    gal.run += 1; clearTimeout(gal.timer);
    gal.asks.forEach(letGo); gal.asks.clear(); letGo(gal.big); gal.big = null;
    gal.key = prm.key;
    if (gal.photos && gal.photos.count !== galCount()) gal.photos = null;
    if (!gal.photos) {
      if (gal.items.length !== ANGLES.length || gal.items[0].name !== 'hero') gal.items = ANGLES.map((name) => ({ name, url: null, sample: false, done: false }));
      // A thumbnail keeps its last good picture only while it is the same metal; another metal's ring is never shown in this one's place.
      gal.items.forEach((it, n) => { it.done = n === 0; it.asking = false; it.noCompose = false; if (it.key !== prm.key) { it.url = null; it.cv = null; it.sample = false; } });
    }
  }
  function galOff() {
    if (!gal.els.length && !gal.key) return;
    gal.run += 1; clearTimeout(gal.timer); gal.asks.forEach(letGo); gal.asks.clear(); letGo(gal.big); gal.big = null;
    gal.key = null; gal.sel = 0; gal.els = []; gal.items = []; gal.photos = null;
    if (thumbsBox) { thumbsBox.replaceChildren(); thumbsBox.hidden = true; }
    metalThumbsFor = null;
    galView();
  }
  // One picture from the door: loads = ready. A failure is asked about once more, as text, to learn why (429, 422 with samples, anything else).
  function galFetch(url, run, cb) {
    const img = new Image(); let over = false; let t = null;
    const end = (r) => { if (over || run !== gal.run) return; over = true; clearTimeout(t); cb(r); };
    t = setTimeout(() => { letGo(img); end({ fail: true }); }, GAL.giveUpMs);
    img.onload = () => (img.decode ? img.decode() : Promise.resolve()).then(() => { fitOf(img, url); end({ url }); }, () => end({ url }));
    img.onerror = () => {
      if (run !== gal.run) return;
      fetch(url, { credentials: 'same-origin' }).then(async (r) => {
        if (r.ok) return { url };
        if (r.status === 429) return { rate: true };
        let b = null;
        if (r.status === 422) { try { b = await r.json(); } catch (_e) { b = null; } }
        return b && b.sample ? { sample: Number(b.samples) || 0 } : { fail: true };
      }).catch(() => ({ fail: true })).then(end);
    };
    img.src = url;
    return img;
  }
  // After the settle and when the browser is idle: the angles of the mix the shopper has stopped on are asked even while the strip is off screen (Henry:
  // "nothing is preloaded"), so they are ready when the shopper scrolls up; the next change cancels them.
  function galPoke() {
    clearTimeout(gal.timer);
    gal.timer = setTimeout(() => { if (window.requestIdleCallback) window.requestIdleCallback(() => galPump(), { timeout: 1500 }); else galPump(); }, GAL.settleMs);
  }
  // Two requests in flight at most (the contract's allowance): the angle the shopper has picked first, then the strip left to right. The big view's own
  // request counts as one of the two.
  function galPump(composeOnly) {
    if (!composeOnly) clearTimeout(gal.timer);
    const prm = galParams();
    if (!prm || prm.key !== gal.key || gal.photos || gal.stopKey === gal.key || !galHeroReady(prm)) return;
    const run = gal.run;
    const cap = gal.big ? 1 : 2;
    const cp = composeParams(chosen[axes.findIndex((x) => x.metal)]);
    [gal.sel, 1, 2, 3, 4].filter((n, i, all) => n > 0 && all.indexOf(n) === i).forEach((n) => {
      const it = gal.items[n];
      if (!it || it.done || it.asking) return;
      // Drawn in the browser where the manifest has this angle's layers: no request to Stuller, so no wait on the door and no allowance spent.
      if (!it.noCompose && angleComposable(ANGLES[n], cp)) {
        it.asking = true;
        composeAngle(ANGLES[n], cp).then((c) => {
          if (run !== gal.run) return;
          it.asking = false; it.cv = c; it.key = prm.key; it.url = null; it.sample = false; it.done = true;
          it.last = { url: null, cv: c, metal: prm.metal };
          galSettled();
        }).catch(() => {
          if (run !== gal.run) return;
          it.asking = false;
          it.noCompose = true; galPump();
        });
        return;
      }
      if (composeOnly || gal.asks.size >= cap) return;
      it.asking = true;
      const img = galFetch(`${prm.base}&angle=${ANGLES[n]}&width=${GAL.thumbW}`, run, (r) => { gal.asks.delete(img); it.asking = false; galResult(prm, n, r, run); });
      gal.asks.add(img);
    });
  }
  function galSettled() { galThumbs(); galView(); galPump(); }
  function galResult(prm, n, r, run) {
    const it = gal.items[n];
    if (r.url) { it.url = r.url; it.key = prm.key; it.last = { url: r.url, cv: null, metal: prm.metal }; it.sample = false; it.done = true; if (n === gal.sel) galBig(n); return galSettled(); }
    if (r.rate) {
      // Over the allowance: what is shown stays, the rest wear the label, and this mix is not asked for again.
      gal.stopKey = gal.key;
      gal.items.forEach((x, k) => { if (k > 0 && !x.done && x.url) x.sample = true; });
      return galSettled();
    }
    if (r.sample > 0 && cfg.photoUrl) {
      const count = galCount();
      gal.photos = { count };
      gal.items = Array.from({ length: Math.min(r.sample, GAL.most) }, (_x, k) => ({
        name: 'photo', url: `${cfg.photoUrl}?product=${encodeURIComponent(String(data.productId || cfg.productId))}&count=${count}&n=${k}&width=${GAL.thumbW}`, sample: true, done: true,
      }));
      gal.sel = Math.min(gal.sel, gal.items.length - 1);
      return galSettled();
    }
    // Not drawn for this mix: the same angle at the opening stones, else the picture the page holds, labelled and kept.
    const settle = (url) => { it.url = url || it.url || (mainImage && mainImage.getAttribute('src')) || null; it.key = prm.key; it.sample = true; it.done = true; galSettled(); };
    const opening = galOpening();
    if (!opening || opening === prm.base) return settle(null);
    it.asking = true;
    const img = galFetch(`${opening}&angle=${ANGLES[n]}&width=${GAL.thumbW}`, run, (o) => { gal.asks.delete(img); it.asking = false; settle(o.url || null); });
    gal.asks.add(img);
    return undefined;
  }
  function galThumbs() {
    if (!gal.els.length || !gal.els[0].isConnected || gal.els.length !== gal.items.length) {
      gal.els = gal.items.map((_it, n) => {
        const b = el('button', { type: 'button', class: 'djh-feed__angle is-ph', 'aria-pressed': 'false' }, el('img', { alt: '', width: '72', height: '72', decoding: 'async' }));
        b.addEventListener('click', () => galSelect(n));
        b.addEventListener('keydown', (e) => {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
          e.preventDefault();
          const next = gal.els[Math.min(gal.els.length - 1, Math.max(0, n + (e.key === 'ArrowRight' ? 1 : -1)))];
          if (next) next.focus();
        });
        return b;
      });
      thumbsBox.replaceChildren(...gal.els);
    }
    const total = gal.items.length;
    gal.items.forEach((it, n) => {
      const b = gal.els[n]; const img = b.firstChild;
      // The first tile is the picture in the frame. When the frame is the browser's own drawing, the tile is a small copy of it (a canvas: nothing leaves the page),
      // not the <img> beneath it, which still holds the opening ring.
      // After a change a tile keeps the picture it had (the same metal) until its own new one is drawn, then swaps: Henry saw every tile go to an empty square on each stone change.
      const curMetal = (galParams() || {}).metal;
      const held = n > 0 && !it.cv && !it.url && it.last && it.last.metal === curMetal ? it.last : null;
      const srcCv = n === 0 ? (!gal.photos && cmp.cv && cmp.shown ? cmp.cv : null) : (it.cv && it.key === gal.key ? it.cv : (held && held.cv) || null);
      const mini = !!srcCv;
      let mc = b.querySelector('canvas');
      if (mini) {
        if (!mc) { mc = el('canvas', { width: '144', height: '144', 'aria-hidden': 'true' }); mc.style.cssText = 'display:block;width:100%;height:100%'; b.append(mc); }
        const cx = mc.getContext('2d'); cx.fillStyle = '#fff'; cx.fillRect(0, 0, 144, 144); cx.drawImage(srcCv, 0, 0, 144, 144);
        if (held && n > 0) b.dataset.held = '1'; else delete b.dataset.held;
        img.style.display = 'none'; b.classList.remove('is-ph'); b.classList.toggle('is-active', n === gal.sel); b.setAttribute('aria-pressed', n === gal.sel ? 'true' : 'false'); b.tabIndex = n === gal.sel ? 0 : -1;
        b.setAttribute('aria-label', fill(m.thumb, { N_I: String(n + 1), N_T: String(total) }));
        return;
      }
      if (mc) { mc.remove(); img.style.display = ''; }
      const src = n === 0 && !gal.photos ? (mainImage && mainImage.getAttribute('src')) || null : it.url || (held && held.url) || null;
      if (src && img.getAttribute('src') !== src) {
        img.src = src;
      } else if (!src && img.hasAttribute('src')) img.removeAttribute('src');
      b.classList.toggle('is-ph', !src);
      b.classList.toggle('is-active', n === gal.sel);
      b.setAttribute('aria-pressed', n === gal.sel ? 'true' : 'false');
      b.setAttribute('aria-label', fill(m.thumb, { N_I: String(n + 1), N_T: String(total) }));
      b.tabIndex = n === gal.sel ? 0 : -1;
      if (it.sample) b.title = m.sampleNote || 'Shown with sample stones'; else b.removeAttribute('title');
      if (held && n > 0) b.dataset.held = '1'; else delete b.dataset.held;
    });
    thumbsBox.hidden = false;
  }
  // The big view over the photo: an angle (or any photo of a sample-only design) lies over the hero's frame; the hero shows the frame itself.
  function galView() {
    const box = (mainImage || document.querySelector('.djh-product__placeholder') || {}).parentElement;
    if (!box) return;
    if (!gal.view) {
      if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
      // The overlay is scaled to the file's framing like every picture, so it sits on a white backdrop of the frame's own size: without it the picture beneath showed around its edges.
      gal.back = el('div', { class: 'djh-feed__galback', hidden: true });
      gal.view = el('img', { class: 'djh-feed__galview', alt: '', hidden: true });
      gal.viewCv = el('canvas', { class: 'djh-feed__galview djh-feed__galcv', role: 'img', 'aria-hidden': 'true', hidden: true });
      gal.label = el('p', { class: 'djh-feed__galsample', hidden: true }, m.sampleNote || 'Shown with sample stones');
      box.append(gal.back, gal.view, gal.viewCv, gal.label);
      box.addEventListener('touchstart', (e) => { const t = e.touches[0]; gal.touch = t ? { x: t.clientX, y: t.clientY } : null; }, { passive: true });
      box.addEventListener('touchend', (e) => {
        const t = e.changedTouches[0]; const s = gal.touch; gal.touch = null;
        if (!s || !t || !gal.els.length) return;
        const dx = t.clientX - s.x; const dy = t.clientY - s.y;
        if (Math.abs(dx) > 40 && Math.abs(dx) > 1.5 * Math.abs(dy)) galSelect(Math.min(gal.items.length - 1, Math.max(0, gal.sel + (dx < 0 ? 1 : -1))));
      }, { passive: true });
    }
    const it = gal.items[gal.sel];
    // The chosen angle stays chosen through a change: until its picture of the new stones has arrived the one the shopper was looking at is held (if it is the
    // same metal; another metal's picture is never held, a soft white frame stands in), then it is swapped for the new one.
    const cur = galParams();
    const held = it && !it.url && !it.cv && it.last && cur && it.last.metal === cur.metal ? it.last : null;
    // A composed angle is a canvas; a door picture an <img>. Either one, current or held.
    const cvNow = it && it.cv && it.key === gal.key ? it.cv : (held && held.cv) || null;
    const src = it ? it.url || (held && held.url) || null : null;
    const wants = !!it && gal.els.length > 0 && (gal.sel > 0 || !!gal.photos);
    const on = wants && (!!src || !!cvNow);
    gal.back.hidden = !wants;
    gal.view.hidden = !(on && !cvNow);
    gal.viewCv.hidden = !(on && !!cvNow);
    // The page already says it under the photo when it opens on the one sample photo (71087); the label is not said twice.
    const said = !!(strip && !strip.hidden && strip.querySelector('.djh-feed__strip-note:not(.is-off)'));
    gal.label.hidden = !(on && it.sample) || said;
    if (on && cvNow) {
      const cv = gal.viewCv;
      if (cv.width !== cvNow.width) { cv.width = cvNow.width; cv.height = cvNow.height; }
      cv.getContext('2d').drawImage(cvNow, 0, 0);
      cv.style.transform = fitCss(`composed:${ANGLES[gal.sel]}:${cur ? cur.metal : ''}`, cvNow);
    } else if (on) {
      const url = gal.bigFor && gal.bigFor.n === gal.sel && gal.bigFor.key === gal.key ? gal.bigFor.url : src;
      if (gal.view.getAttribute('src') !== url) { gal.view.style.transform = fitCss(url); gal.view.src = url; }
      gal.view.alt = mainImage ? mainImage.alt : '';
    }
  }
  // The selected angle's own 1200 px picture on a wide screen: the second request in flight (the warm lists usually hold it).
  function galBig(n) {
    const prm = galParams(); const it = gal.items[n];
    if (!prm || !it || n < 1 || gal.photos || it.sample || it.cv || galWide() <= GAL.thumbW) return;
    letGo(gal.big);
    const run = gal.run;
    gal.big = galFetch(`${prm.base}&angle=${ANGLES[n]}&width=${galWide()}`, run, (r) => { gal.big = null; if (r.url && gal.sel === n) { gal.bigFor = { n, key: gal.key, url: r.url }; galView(); } });
  }
  function galSelect(n) {
    const it = gal.items[n];
    if (!it || (!it.url && !it.cv && !(n === 0 && !gal.photos))) return;
    gal.sel = n; gal.bigFor = null; letGo(gal.big); gal.big = null;
    galThumbs(); galView();
    galBig(n);
  }
  // Called from every paint. False where the door cannot draw this ring, and the page keeps its own row.
  function paintGallery() {
    const prm = galParams();
    if (!prm) return false;
    if (prm.key !== gal.key) galReset(prm);
    galThumbs(); galView(); galPump(true); galPoke(); warmBind();
    return true;
  }

  // ASKING BEFORE THE TAP (Henry, 2026-11-04: "as if nothing is preloaded"). A cold picture takes about 3 s at Stuller, so the shopper who rests on a
  // stone or a metal for a moment (a pointer, not a finger) has that picture asked for in the meantime; the tap then finds it kept. Hero only, at the
  // device's width, the address the tap would ask; a handful a minute at most, never on data saver, never once the door has said slow down.
  const warm = { bound: false, timer: null, seen: new Set(), at: [] };
  function warmBind() {
    if (warm.bound || !stoneApi) return;
    warm.bound = true;
    document.addEventListener('pointerout', () => clearTimeout(warm.timer));
    document.addEventListener('pointerover', (e) => {
      if (e.pointerType === 'touch' || (navigator.connection && navigator.connection.saveData) || !e.target.closest) return;
      const opt = e.target.closest('.djh-feed__stone-row .djh-gl__option, [data-axis] .djh-product__swatch');
      if (!opt) return;
      clearTimeout(warm.timer);
      warm.timer = setTimeout(() => {
        const mAt = axes.findIndex((a) => a.metal);
        const sAt = axes.findIndex((a) => a.positions === 'count');
        if (mAt < 0 || sAt < 0 || !stoneApi.mixed() || gal.stopKey) return;
        let prm = null;
        if (opt.classList.contains('djh-product__swatch')) {
          if (opt.closest('[data-axis]').getAttribute('data-axis') !== String(mAt)) return;
          prm = doorAddress(opt.getAttribute('data-value'), stoneApi.picks(), sAt);
        } else {
          const rowsEl = [...document.querySelectorAll('[data-axis] .djh-feed__stone-row')];
          const at = rowsEl.indexOf(opt.closest('.djh-feed__stone-row'));
          const picks = stoneApi.picks();
          if (at < 0 || at >= picks.length) return;
          picks[at] = opt.getAttribute('data-value');
          prm = doorAddress(chosen[mAt], picks, sAt);
        }
        const now = Date.now();
        warm.at = warm.at.filter((t) => now - t < 60000);
        if (!prm || drawn.has(prm.key) || warm.seen.has(prm.key) || warm.at.length >= 6) return;
        warm.seen.add(prm.key); warm.at.push(now);
        new Image().src = prm.url;
      }, 250);
    });
  }
  // QA-ONLY TIMING READOUT (Henry, 2026-11-04: tap to new picture painted, cold and warm, on a real phone). Behind ?djhperf=1 AND a dev store (the
  // snippet passes cfg.perf only there), so nothing on a customer-facing store. It measures from the pointer-down on a stone to the frame after
  // the new picture is on screen, and names the path: compose (drawn in the browser), door (the server's picture), kept (nothing could be drawn).
  // 'warm' = the picture or layers were already in memory or the browser's cache; 'cold' = they were fetched for this tap. Numbers go to a small
  // badge, the console, and window.__djhPerf.
  const PERF_ON = !!cfg.perf && /[?&]djhperf=1(?:&|$)/.test(location.search);
  const perf = { tapAt: 0, want: null, log: [], badge: null };
  function perfDone(key, path, extra) {
    if (!PERF_ON || !perf.want || perf.want.key !== key) return;
    const w = perf.want; perf.want = null;
    const finish = () => requestAnimationFrame(() => requestAnimationFrame(() => {
      let warm = 'n/a';
      if (path === 'compose') warm = cmp.net === w.net ? 'warm' : 'cold';
      else if (path === 'door') {
        const e = performance.getEntriesByName(extra && extra.url ? new URL(extra.url, location.href).href : '').pop();
        warm = w.inMemory || (e && e.transferSize === 0) ? 'warm' : 'cold';
      }
      const row = { ms: Math.round(performance.now() - w.t0), path, temp: warm };
      perf.log.push(row);
      (window.__djhPerf = window.__djhPerf || []).push(row);
      try { console.info('[djhperf]', row.ms + ' ms', row.temp, row.path); } catch (_e) { /* no console */ }
      if (!perf.badge) {
        perf.badge = el('div', { 'aria-hidden': 'true', style: 'position:fixed;left:8px;bottom:8px;z-index:2147483000;max-width:92vw;padding:6px 8px;border-radius:6px;background:rgba(0,0,0,.78);color:#fff;font:12px/1.35 ui-monospace,Menlo,monospace;pointer-events:none;white-space:pre' });
        document.body.append(perf.badge);
      }
      perf.badge.textContent = perf.log.slice(-6).reverse().map((r, i) => `${i ? ' ' : '>'} ${String(r.ms).padStart(5)} ms  ${r.temp.padEnd(4)}  ${r.path}`).join('\n');
    }));
    finish();
  }
  function perfWant(key, path, inMemory) {
    if (!PERF_ON) return;
    if (!key) { perf.want = null; return; }
    if (perf.want && perf.want.key === key) { perf.want.path = path; return; }
    const now = performance.now();
    perf.want = { key, path, t0: now - perf.tapAt < 5000 ? perf.tapAt : now, net: cmp.net, inMemory: !!inMemory };
  }
  if (PERF_ON) {
    const tap = () => { perf.tapAt = performance.now(); };
    document.addEventListener('pointerdown', (e) => { if (e.target && e.target.closest && e.target.closest('.djh-gl__option')) tap(); }, true);
    document.addEventListener('keydown', (e) => { if (e.key === 'Enter' && e.target && e.target.closest && e.target.closest('.djh-gl__option')) tap(); }, true);
    document.addEventListener('click', (e) => { if (e.target && e.target.closest && e.target.closest('.djh-gl__option') && performance.now() - perf.tapAt > 800) tap(); }, true);
  }

  // THE PICTURE OF A MIXED SET, DRAWN IN THE BROWSER (D-383, Henry 2026-11-04; QA only). When the picker file carries composeUrl, a changed mix is
  // drawn into a canvas in the photo's frame instead of being asked of the door: the base picture of the metal at this count, then for each stone
  // place a stone layer recolored by that stone's own curves (CATALOG's manifest v1, the layer files beside it). A stone change redraws only the
  // place that changed (and whatever overlaps its rectangle); nothing is asked of a server, so there is no "Drawing your ring" line. Any failure
  // (manifest, a layer, a size, a decode, a timeout) marks composing failed for the visit and the page falls back to the door, then to the kept picture.
  // Same-stone rings and the openings stay the pre-drawn pictures; only a mix of two or more different stones is composed.
  const COMPOSE = { timeoutMs: 8000, requestMs: 6000, retryMs: 300, minPx: 240, idleMs: 400, basesKept: 4 };
  const cmp = { net: 0, man: null, manP: null, failed: false, T: 0, cv: null, ctx: null, work: null, shown: null, wantKey: null, seq: 0, base: new Map(), place: new Map(), lut: new Map(), ang: new Map() };
  const composeTimeout = (p) => Promise.race([p, new Promise((_, no) => setTimeout(() => no(new Error('timeout')), COMPOSE.timeoutMs))]);
  // A layer is fetched, never put in an <img>: the response is read as a blob, so the canvas is never tainted whatever the browser's CORS handling
  // of images (Safari), and `force-cache` takes a hash-named layer from the browser's cache without asking the server whether it changed, so a
  // repeat visit costs no request even where the store answers `no-cache`. One retry after a short wait; each try has its own timeout.
  const sleep = (ms) => new Promise((ok) => setTimeout(ok, ms));
  function decodePicture(blob) {
    if (window.createImageBitmap) return createImageBitmap(blob).then((bm) => ({ src: bm, w: bm.width, h: bm.height, free: () => bm.close() }));
    return new Promise((ok, no) => {
      const u = URL.createObjectURL(blob); const i = new Image();
      i.onload = () => ok({ src: i, w: i.naturalWidth, h: i.naturalHeight, free: () => URL.revokeObjectURL(u) });
      i.onerror = () => { URL.revokeObjectURL(u); no(new Error('image')); };
      i.src = u;
    });
  }
  async function loadPicture(url) {
    for (let attempt = 0; ; attempt++) {
      cmp.net += 1;
      const ctl = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = setTimeout(() => { if (ctl) ctl.abort(); }, COMPOSE.requestMs);
      try {
        const r = await fetch(url, { cache: 'force-cache', mode: 'cors', credentials: 'omit', signal: ctl ? ctl.signal : undefined });
        if (!r.ok) throw new Error('http');
        return await decodePicture(await r.blob());
      } catch (e) {
        if (attempt >= 1) throw e;
        await sleep(COMPOSE.retryMs);
      } finally {
        clearTimeout(timer);
      }
    }
  }
  function pixelsOf(pic, w, h) {
    const c = document.createElement('canvas'); c.width = w; c.height = h;
    const x = c.getContext('2d', { willReadFrequently: true });
    x.drawImage(pic.src, 0, 0, w, h);
    pic.free();
    return x.getImageData(0, 0, w, h);
  }
  function getManifest() {
    if (!cmp.manP) {
      const ask = () => fetch(data.composeUrl, { credentials: 'omit' }).then((r) => { if (!r.ok) throw new Error('manifest'); return r.json(); });
      cmp.manP = composeTimeout(ask().catch(() => sleep(COMPOSE.retryMs).then(ask))).then((j) => {
        if (!j || j.v !== 1 || !(j.size > 0) || !j.metals || !j.stones || !j.counts) throw new Error('manifest');
        cmp.man = j;
        return j;
      }).catch((e) => { cmp.manP = null; throw e; });
    }
    return cmp.manP;
  }
  // Every file the manifest names is relative to its `base` (an absolute folder: the layers live in another bucket than the manifest); a manifest
  // with no base has its files beside it.
  const abs = (u) => new URL(u, (cmp.man && cmp.man.base) || data.composeUrl).href;
  // The visible canvas is as wide as the frame is in device pixels (at most two per CSS pixel), never beyond the layers' own size, so a phone
  // draws a picture a third the weight of a desktop's. The layers are scaled to it once, when they are decoded.
  function targetPx(man) {
    if (cmp.T) return cmp.T;
    const frame = (mainImage && mainImage.parentElement) || null;
    const css = frame ? frame.getBoundingClientRect().width : 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    cmp.T = Math.min(man.size, Math.max(COMPOSE.minPx, Math.round((css || man.size) * dpr)));
    return cmp.T;
  }
  // A "scene" is the 3/4 hero (counts[c]) or one of the other angles (counts[c].angles[name]: upright, front, side), each with its own base and its own places and boxes.
  const sceneOf = (man, count, angle) => { const c = man.counts[count]; return !c ? null : angle === 'hero' ? c : (c.angles && c.angles[angle]) || null; };
  async function getBase(man, count, metalKey, angle = 'hero') {
    const k = angle === 'hero' ? `${count}|${metalKey}` : `${angle}|${count}|${metalKey}`;
    if (!cmp.base.has(k)) {
      const T = targetPx(man);
      const sc = sceneOf(man, count, angle);
      const u = sc && sc.base && sc.base[metalKey];
      if (!u || !u.u) throw new Error('base');
      const p = loadPicture(abs(u.u)).then((pic) => { if (pic.w !== man.size || pic.h !== man.size) { pic.free(); throw new Error('size'); } return pixelsOf(pic, T, T); });
      cmp.base.set(k, p);
      p.catch(() => cmp.base.delete(k));
      while (cmp.base.size > COMPOSE.basesKept + 6) cmp.base.delete(cmp.base.keys().next().value);
    }
    return cmp.base.get(k);
  }
  // One place's layers at the canvas size: its rectangle, the stone plane (RGB) and the transparency plane (one byte a pixel).
  async function getPlace(man, count, n, kind, angle = 'hero') {
    const k = angle === 'hero' ? `${count}|${n}|${kind}` : `${angle}|${count}|${n}|${kind}`;
    if (!cmp.place.has(k)) {
      const T = targetPx(man); const s = T / man.size;
      const sc = sceneOf(man, count, angle);
      const p = sc && sc.places.find((x) => x.n === n);
      const L = p && p[kind];
      if (!L || !L.S || !L.A || !Array.isArray(p.box)) throw new Error('place');
      const [x0, y0, x1, y1] = p.box;
      const bx0 = Math.floor(x0 * s); const by0 = Math.floor(y0 * s); const bx1 = Math.min(T, Math.ceil(x1 * s)); const by1 = Math.min(T, Math.ceil(y1 * s));
      const w = bx1 - bx0; const h = by1 - by0;
      const entry = Promise.all([loadPicture(abs(L.S.u)), loadPicture(abs(L.A.u))]).then(([S, A]) => {
        if (S.w !== x1 - x0 || S.h !== y1 - y0 || A.w !== x1 - x0 || A.h !== y1 - y0) { S.free(); A.free(); throw new Error('size'); }
        const sd = pixelsOf(S, w, h).data; const ad = pixelsOf(A, w, h).data; const alpha = new Uint8ClampedArray(w * h);
        for (let i = 0; i < w * h; i++) alpha[i] = ad[i * 4];
        return { x0: bx0, y0: by0, x1: bx1, y1: by1, w, h, S: sd, A: alpha };
      });
      cmp.place.set(k, entry);
      entry.catch(() => cmp.place.delete(k));
    }
    return cmp.place.get(k);
  }
  // A stone's colour curves, as CATALOG exports them: a natural cubic spline through "in,out" points, end values held outside the first and last
  // point, clipped to 0-255 after each stage; the channel's own curve first, then the master curve. One 256-entry table a channel.
  function spline(str) {
    const pts = str.split('.').map((q) => q.split(',').map(Number)).sort((a, b) => a[0] - b[0]).filter((q, i, a) => i === 0 || q[0] !== a[i - 1][0]);
    const n = pts.length; const x = pts.map((q) => q[0]); const y = pts.map((q) => q[1]);
    if (n === 1) return () => y[0];
    const h = []; const a = []; const l = [1]; const mu = [0]; const z = [0]; const c = new Array(n).fill(0); const b = []; const dd = [];
    for (let i = 0; i < n - 1; i++) h[i] = x[i + 1] - x[i];
    for (let i = 1; i < n - 1; i++) a[i] = 3 * (y[i + 1] - y[i]) / h[i] - 3 * (y[i] - y[i - 1]) / h[i - 1];
    for (let i = 1; i < n - 1; i++) { l[i] = 2 * (x[i + 1] - x[i - 1]) - h[i - 1] * mu[i - 1]; mu[i] = h[i] / l[i]; z[i] = (a[i] - h[i - 1] * z[i - 1]) / l[i]; }
    for (let j = n - 2; j >= 0; j--) { c[j] = z[j] - (mu[j] || 0) * c[j + 1]; b[j] = (y[j + 1] - y[j]) / h[j] - h[j] * (c[j + 1] + 2 * c[j]) / 3; dd[j] = (c[j + 1] - c[j]) / (3 * h[j]); }
    return (v) => {
      const t0 = Math.min(Math.max(v, x[0]), x[n - 1]);
      let i = n - 2; for (let k = 0; k < n - 1; k++) if (t0 <= x[k + 1]) { i = k; break; }
      const t = t0 - x[i];
      return Math.min(Math.max(y[i] + b[i] * t + c[i] * t * t + dd[i] * t * t * t, 0), 255);
    };
  }
  function lutsOf(curves) {
    const id = (v) => v; const master = curves && curves.curves ? spline(curves.curves) : id;
    return ['r', 'g', 'b'].map((ch) => { const f = curves && curves[ch] ? spline(curves[ch]) : id; const t = new Uint8ClampedArray(256); for (let v = 0; v < 256; v++) t[v] = master(f(v)); return t; });
  }
  function getLuts(origin, key, info) {
    const k = `${origin}|${key}`;
    if (!cmp.lut.has(k)) cmp.lut.set(k, lutsOf(info.curves));
    return cmp.lut.get(k);
  }
  // out = out * (1 - a) + a * LUT(stone plane), inside the place's rectangle and inside `clip` (a rectangle on the canvas).
  function blendPlace(d, T, P, lut, clip) {
    const xa = Math.max(P.x0, clip.x0); const xb = Math.min(P.x1, clip.x1); const ya = Math.max(P.y0, clip.y0); const yb = Math.min(P.y1, clip.y1);
    for (let y = ya; y < yb; y++) {
      for (let x = xa; x < xb; x++) {
        const i = (y - P.y0) * P.w + (x - P.x0); const a = P.A[i];
        if (!a) continue;
        const q = (y * T + x) * 4; const af = a / 255; const j = i * 4;
        d[q] = d[q] * (1 - af) + af * lut[0][P.S[j]];
        d[q + 1] = d[q + 1] * (1 - af) + af * lut[1][P.S[j + 1]];
        d[q + 2] = d[q + 2] * (1 - af) + af * lut[2][P.S[j + 2]];
      }
    }
  }
  // What the picture shows, in the page's own words: the product's name and the stones left to right (no new wording). The canvas carries it as its
  // label, and a visually hidden status line says it when the stones change, so a screen reader hears what was chosen.
  const altBase = mainImage ? (mainImage.getAttribute('alt') || '') : '';
  const stoneWords = () => stoneApi.picks().map((v) => gemLabel(v)).join(', ');
  // What the picture shows, said from the state, never from Shopify's own alt (which described a 3-stone ring over four other stones): metal, how many, which.
  const composeLabel = () => {
    const mAt = axes.findIndex((a) => a.metal);
    const metal = mAt >= 0 && chosen[mAt] ? `${shown(chosen[mAt])}, ` : '';
    const n = stoneApi ? stoneApi.picks().length : 0;
    return `${metal}${n > 1 ? `${n} stones` : '1 stone'}: ${stoneWords()}`;
  };
  // The same for a picture from the door: the photo's alt and the hidden status line follow the stones while the mix is a changed one (any path).
  let said = null;
  function announceStones(on) {
    if (!mainImage) return;
    if (on) {
      const words = stoneWords();
      if (mainImage.getAttribute('alt') !== composeLabel()) mainImage.setAttribute('alt', composeLabel());
      if (!said) { said = el('p', { class: 'djh-visually-hidden', role: 'status', 'aria-live': 'polite' }); mainImage.parentElement.append(said); }
      if (said.textContent !== words) said.textContent = words;
    } else {
      if (mainImage.getAttribute('alt') !== altBase) mainImage.setAttribute('alt', altBase);
      if (said && said.textContent) said.textContent = '';
    }
  }
  function ensureCanvas(T) {
    if (cmp.cv) return;
    const box = mainImage.parentElement;
    cmp.cv = el('canvas', { class: 'djh-feed__compose', width: String(T), height: String(T), role: 'img', 'aria-label': composeLabel() });
    cmp.ctx = cmp.cv.getContext('2d', { willReadFrequently: true });
    mainImage.insertAdjacentElement('afterend', cmp.cv);
    box.dataset.djhCompose = '1';
  }
  // The mix the shopper is on, as the manifest names things: null while it is not a composable ask (no composeUrl, composing failed, fewer than two
  // different stones, a stone with no key).
  function composeParams(metalValue) {
    const mAt = axes.findIndex((a) => a.metal);
    const sAt = axes.findIndex((a) => a.positions === 'count');
    const cAt = axes.findIndex((a) => a.count);
    if (!data.composeUrl || cmp.failed || !mainImage || mAt < 0 || sAt < 0 || cAt < 0 || !metalValue || chosen[cAt] == null) return null;
    const picks = stoneApi.picks();
    const keys = picks.map((v) => axes[sAt].keyOf.get(v) || null);
    if (keys.length < 2 || keys.some((k) => !k) || new Set(keys).size < 2) return null;
    const origin = /^lab/i.test(picks[0]) ? 'lab' : 'natural';
    return { key: `${metalValue}~${chosen[cAt]}~${origin}~${keys.join('|')}`, metal: metalValue, count: String(chosen[cAt]), origin, stones: keys };
  }
  // Everything a draw needs, loaded: throws on anything the manifest cannot honestly draw.
  async function composeLoad(cp, withTimeout, angle = 'hero') {
    const man = await getManifest();
    const metalKey = man.metals[cp.metal];
    const cs = sceneOf(man, cp.count, angle);
    const table = man.stones[cp.origin];
    if (!metalKey || !cs || !table || !Array.isArray(cs.places) || cs.places.length !== cp.stones.length) throw new Error('manifest');
    const infos = cp.stones.map((k) => table[k]);
    if (infos.some((i) => !i || (i.kind !== 'faceted' && i.kind !== 'diamonds'))) throw new Error('stone');
    // A place the angle hides (visible: false: the stones behind the first in the edge-on view) has a dummy box and no layers: it is not asked for and not drawn.
    const all = Promise.all([getBase(man, cp.count, metalKey, angle), ...infos.map((info, i) => (cs.places[i] && cs.places[i].visible === false ? null : getPlace(man, cp.count, i + 1, info.kind, angle)))]);
    const [base, ...places] = await (withTimeout ? composeTimeout(all) : all);
    return { man, base, places, luts: cp.stones.map((k, i) => getLuts(cp.origin, k, infos[i])) };
  }
  function composeDraw(R, cp) {
    const T = targetPx(R.man);
    ensureCanvas(T);
    if (!cmp.work || cmp.work.width !== T) cmp.work = new ImageData(T, T);
    const d = cmp.work.data; const prev = cmp.shown;
    const same = prev && prev.count === cp.count && prev.metal === cp.metal && prev.origin === cp.origin && prev.stones.length === cp.stones.length && prev.base === R.base;
    const rects = [];
    if (!same) {
      d.set(R.base.data);
      const full = { x0: 0, y0: 0, x1: T, y1: T };
      R.places.forEach((P, i) => blendPlace(d, T, P, R.luts[i], full));
      cmp.ctx.putImageData(cmp.work, 0, 0);
    } else {
      cp.stones.forEach((k, i) => { if (prev.stones[i] !== k) rects.push({ x0: R.places[i].x0, y0: R.places[i].y0, x1: R.places[i].x1, y1: R.places[i].y1 }); });
      let ux0 = T; let uy0 = T; let ux1 = 0; let uy1 = 0;
      rects.forEach((r) => {
        // Restore the base inside the changed place's rectangle, then lay down every place whose rectangle reaches it, clipped to it.
        for (let y = r.y0; y < r.y1; y++) { const q = (y * T + r.x0) * 4; d.set(R.base.data.subarray(q, q + (r.x1 - r.x0) * 4), q); }
        R.places.forEach((P, i) => { if (P.x0 < r.x1 && P.x1 > r.x0 && P.y0 < r.y1 && P.y1 > r.y0) blendPlace(d, T, P, R.luts[i], r); });
        ux0 = Math.min(ux0, r.x0); uy0 = Math.min(uy0, r.y0); ux1 = Math.max(ux1, r.x1); uy1 = Math.max(uy1, r.y1);
      });
      if (rects.length) cmp.ctx.putImageData(cmp.work, 0, 0, ux0, uy0, ux1 - ux0, uy1 - uy0);
    }
    cmp.shown = { key: cp.key, count: cp.count, metal: cp.metal, origin: cp.origin, stones: cp.stones.slice(), base: R.base };
    cmp.cv.setAttribute('aria-label', composeLabel());
    if (gal.els.length) { galThumbs(); galPoke(); }
  }
  // The other angles, drawn in the browser from the same kind of layers as the hero (CATALOG's manifest `angles`): out = base * (1 - A) + A * LUT(S) per visible place.
  // An angle with no layers in the manifest, or any failure, is the door's picture as before. (The mirror is NOT the hero flipped: measured on QA, a flipped hero
  // differs from Stuller's mirror picture by 25 to 38 of 255 on average, against 0.2 to 1.0 for the angles drawn from their own layers; it has its own layers.)
  const ANGLE_LAYERS = ['upright', 'front', 'side', 'mirror'];
  function angleComposable(angle, cp) {
    if (!cp || !cmp.man || cmp.failed) return false;
    const c = cmp.man.counts[cp.count];
    return ANGLE_LAYERS.includes(angle) && !!(c && c.angles && c.angles[angle]);
  }
  // One live canvas per angle, kept between changes (like the hero's): a stone change restores the base inside the changed place's rectangle and lays down only the places
  // that reach it, then the tile is updated from it; the ring around the stone is never redrawn. A full draw only when the metal, the count, the origin or the base changed.
  async function composeAngle(angle, cp) {
    let st = cmp.ang.get(angle);
    if (!st) { st = { cv: null, ctx: null, work: null, shown: null, seq: 0 }; cmp.ang.set(angle, st); }
    const mine = ++st.seq;
    const R = await composeLoad(cp, true, angle);
    if (mine !== st.seq) return st.cv;   // a newer change for this angle is on its way: this one is not drawn
    const T = targetPx(R.man);
    if (!st.cv || st.cv.width !== T) {
      st.cv = document.createElement('canvas'); st.cv.width = T; st.cv.height = T; st.cv.dataset.angle = angle;
      st.ctx = st.cv.getContext('2d', { willReadFrequently: true }); st.work = new ImageData(T, T); st.shown = null;
    }
    const d = st.work.data; const prev = st.shown;
    const same = prev && prev.count === cp.count && prev.metal === cp.metal && prev.origin === cp.origin && prev.stones.length === cp.stones.length && prev.base === R.base;
    if (!same) {
      d.set(R.base.data);
      const full = { x0: 0, y0: 0, x1: T, y1: T };
      R.places.forEach((P, i) => { if (P) blendPlace(d, T, P, R.luts[i], full); });
      st.ctx.putImageData(st.work, 0, 0);
    } else {
      const rects = [];
      cp.stones.forEach((k, i) => { const P = R.places[i]; if (prev.stones[i] !== k && P) rects.push({ x0: P.x0, y0: P.y0, x1: P.x1, y1: P.y1 }); });
      let ux0 = T; let uy0 = T; let ux1 = 0; let uy1 = 0;
      rects.forEach((r) => {
        for (let y = r.y0; y < r.y1; y++) { const q = (y * T + r.x0) * 4; d.set(R.base.data.subarray(q, q + (r.x1 - r.x0) * 4), q); }
        R.places.forEach((P, i) => { if (P && P.x0 < r.x1 && P.x1 > r.x0 && P.y0 < r.y1 && P.y1 > r.y0) blendPlace(d, T, P, R.luts[i], r); });
        ux0 = Math.min(ux0, r.x0); uy0 = Math.min(uy0, r.y0); ux1 = Math.max(ux1, r.x1); uy1 = Math.max(uy1, r.y1);
      });
      if (rects.length) st.ctx.putImageData(st.work, 0, 0, ux0, uy0, ux1 - ux0, uy1 - uy0);
    }
    st.shown = { count: cp.count, metal: cp.metal, origin: cp.origin, stones: cp.stones.slice(), base: R.base };
    return st.cv;
  }
  function cancelCompose() { cmp.seq++; cmp.wantKey = null; cmp.shown = null; }
  // Called from every paint with the mix the shopper is on now (null when it is not composable). Draws at once; the picture of the last mix stays
  // in the frame until this one is ready, which is under a couple of hundred milliseconds once the layers are in.
  function wantCompose(cp) {
    if (!cp) { if (cmp.wantKey) cancelCompose(); return; }
    if (cmp.wantKey === cp.key) return;
    cmp.wantKey = cp.key;
    const seq = ++cmp.seq;
    composeLoad(cp, true).then((R) => {
      if (seq !== cmp.seq) return;   // the shopper moved on: a stale draw is dropped
      composeDraw(R, cp);
      paint();
      perfDone(cp.key, 'compose');
    }).catch(() => {
      if (seq !== cmp.seq) return;
      cmp.failed = true; cancelCompose();
      paint();                       // the door takes it from here, then the kept picture
    });
  }
  // THE PICK SURVIVES BACK: the metal, count and stones go into this history entry's state (never the address) when they differ from the opening
  // pick, and the signed first view reads them again when the page is rebuilt. A page restored whole from the back/forward cache needs none of it.
  const pickId = () => String(data.productId || cfg.productId);
  function savedPick() {
    try { const s = history.state && history.state.djhPick; return s && s.p === pickId() ? s : null; } catch (_e) { return null; }
  }
  let savedKey = ''; let saveTimer = null;
  function savePick() {
    if (!stoneApi) return;
    const mAt = axes.findIndex((a) => a.metal); const cAt = axes.findIndex((a) => a.count);
    if (cAt < 0) return;
    const atOpening = openingAt === key(chosen) && stoneApi.pristine();
    const want = atOpening ? '' : JSON.stringify([mAt >= 0 ? chosen[mAt] : null, chosen[cAt], stoneApi.picks()]);
    if (want === savedKey) return;
    savedKey = want;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        const st = Object.assign({}, history.state);
        if (atOpening) delete st.djhPick; else st.djhPick = { p: pickId(), m: mAt >= 0 ? chosen[mAt] : null, c: chosen[cAt], s: stoneApi.picks() };
        history.replaceState(st, '');
      } catch (_e) { /* a browser that refuses: the pick simply does not survive Back */ }
    }, 250);
  }

  // With the page idle, so a change finds its layers in memory instead of asking for them: the manifest; the base of the metal and count the page
  // is on and every place's faceted layers plus the kind the opening stone there uses; and once the shopper has changed a stone (they are
  // exploring), the diamond layers of that count and the base of the other metals at it. A new metal or count is preloaded as soon as it is chosen.
  // A miss here is not a failure; the draw asks again when it is wanted. Bounded: one count's layers and at most a base per metal.
  let preloadedFor = null;
  let baseChain = Promise.resolve();
  function preloadCompose() {
    if (!data.composeUrl || cmp.failed || !stoneApi || !mainImage) return;
    // A visitor who asked the browser to save data, or a tab nobody is looking at, loads nothing ahead of time; a draw asks for what it needs.
    if ((navigator.connection && navigator.connection.saveData) || document.visibilityState === 'hidden') return;
    const mAt = axes.findIndex((a) => a.metal); const sAt = axes.findIndex((a) => a.positions === 'count'); const cAt = axes.findIndex((a) => a.count);
    if (mAt < 0 || sAt < 0 || cAt < 0 || chosen[mAt] == null || chosen[cAt] == null) return;
    const exploring = !stoneApi.pristine();
    const want = `${chosen[mAt]}|${chosen[cAt]}|${exploring ? 1 : 0}`;
    if (want === preloadedFor) return;
    preloadedFor = want;
    const go = () => {
      getManifest().then((man) => {
        const count = String(chosen[cAt]);
        const metalKey = man.metals[chosen[mAt]]; const cs = man.counts[count];
        if (!metalKey || !cs || !Array.isArray(cs.places)) return;
        const origin = /^lab/i.test(stoneApi.picks()[0] || '') ? 'lab' : 'natural';
        const keys = stoneApi.picks().map((v) => axes[sAt].keyOf.get(v) || null);
        const kinds = cs.places.map((p, i) => ((man.stones[origin] || {})[keys[i]] || {}).kind || 'faceted');
        const quiet = () => {};
        // Bases are the heavy decodes (a 1200 px picture is 5.8 MB decoded): preloaded one at a time, so an old phone never holds several at once.
        const base = (k) => { baseChain = baseChain.then(() => getBase(man, count, k)).catch(quiet); };
        base(metalKey);
        cs.places.forEach((p, i) => new Set(['faceted', kinds[i]]).forEach((kind) => getPlace(man, count, p.n, kind).catch(quiet)));
        // The other angles' own layers, after the hero's, one angle at a time: a stone change then draws all four in a few milliseconds.
        ANGLE_LAYERS.forEach((a) => {
          const sc = sceneOf(man, count, a);
          if (!sc) return;
          baseChain = baseChain.then(() => getBase(man, count, metalKey, a)).catch(quiet);
          sc.places.forEach((p, i) => { if (p.visible !== false) new Set(['faceted', kinds[i]]).forEach((kind) => getPlace(man, count, p.n, kind, a).catch(quiet)); });
        });
        if (exploring) {
          cs.places.forEach((p) => getPlace(man, count, p.n, 'diamonds').catch(quiet));
          Object.values(man.metals).filter((k) => k !== metalKey).forEach(base);
        }
      }).catch(() => {});
    };
    (window.requestIdleCallback || ((f) => setTimeout(f, COMPOSE.idleMs)))(go, { timeout: 2000 });
  }

  // A drawn design whose picture for this mix could not be drawn keeps the last picture and says so with the existing words.
  let noteEl = null;
  function photoNote(on) {
    const box = (mainImage || {}).parentElement;
    if (!box) return;
    if (!noteEl) { noteEl = el('p', { class: 'djh-feed__failnote', hidden: true }, m.sampleNote || 'Shown with sample stones'); box.append(noteEl); }
    noteEl.hidden = !on;
  }
  // The new picture is put OVER the held one and faded in (160 ms), then becomes the frame's own: no frame is anything but the previous picture, the new one, or a blend
  // of the two. A picture that is not decoded is never shown; with reduced motion, or no picture to hold, it simply replaces.
  function swapMain(url) {
    const apply = () => { mainImage.style.transform = fitCss(url); mainImage.src = url; mainImage.removeAttribute('srcset'); };
    const box = mainImage.parentElement;
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !box || !mainImage.complete || !mainImage.naturalWidth || box.classList.contains('djh-feed--hold')) { apply(); return; }
    if (mainImage._xf) { mainImage._xf.remove(); mainImage._xf = null; }
    const xf = new Image();
    xf.className = `${mainImage.className} djh-feed__xfade`; xf.alt = ''; xf.style.transform = fitCss(url); xf.src = url;
    mainImage._xf = xf;
    (xf.decode ? xf.decode() : Promise.resolve()).catch(() => {}).then(() => {
      if (mainImage._xf !== xf) return;
      box.append(xf);
      requestAnimationFrame(() => requestAnimationFrame(() => xf.classList.add('is-in')));
      setTimeout(() => {
        if (mainImage._xf !== xf) return;
        apply();
        (mainImage.decode ? mainImage.decode() : Promise.resolve()).catch(() => {}).then(() => { if (mainImage._xf === xf) { xf.remove(); mainImage._xf = null; } });
      }, 190);
    });
  }
  let strip = null;
  const onScreen = { metal: null };
  function paintStonePhoto() {
    // A product with no media at all (12609's draft has none) has no main image: the theme draws a placeholder instead, and the strip
    // takes its place.
    const anchor = mainImage || document.querySelector('.djh-product__placeholder');
    if (!anchor) return;
    if (!strip) { strip = el('div', { class: 'djh-feed__strip', hidden: true }); anchor.insertAdjacentElement('afterend', strip); }
    const mixed = stoneApi.mixed();
    const stoneAt3 = axes.findIndex((a) => a.positions === 'count');
    const mAt = axes.findIndex((a) => a.metal);
    const cAt = axes.findIndex((a) => a.count);
    // The file keys the picture the way it keys `img`: by the position of each value in the axis as the file lists it ("0|3" = the
    // first metal, the fourth count). A key by value is read too.
    const at = (k) => data.axes[k].values.findIndex((v) => v.value === chosen[k]);
    const hero = data.hero && (data.hero[`${at(mAt)}|${at(cAt)}`] || data.hero[`${chosen[mAt]}|${chosen[cAt]}`]);
    const pristine = stoneApi.pristine();
    const showHero = (mixed || data.open === 'same') && pristine && hero;
    if (showHero && mainImage) { mainImage.src = hero; mainImage.removeAttribute('srcset'); }
    // A changed mix with a picture drawn for it (or being drawn: the strip stays until it arrives).
    // composeUrl present: the browser draws the mix (D-383); the door is asked only once composing has failed.
    const cp = mixed && !pristine ? composeParams(chosen[mAt]) : null;
    wantCompose(cp);
    const prm = mixed && !pristine && !cp ? drawParams(chosen[mAt]) : null;
    perfWant(cp ? cp.key : prm ? prm.key : null, cp ? 'compose' : 'door', prm ? drawn.has(prm.key) : false);
    wantDraw(prm);
    const composed = !!cp && !!cmp.shown && !!cmp.cv;
    if (cmp.cv) cmp.cv.parentElement.classList.toggle('djh-feed--composed', composed);
    const drawnUrl = prm && drawn.get(prm.key);
    const showDrawn = (!!drawnUrl && !!mainImage) || composed;
    if (drawnUrl && mainImage && mainImage.getAttribute('src') !== drawnUrl) {
      swapMain(drawnUrl);
      (mainImage.decode ? mainImage.decode() : Promise.resolve()).catch(() => {}).then(() => perfDone(prm.key, 'door', { url: drawnUrl }));
    }
    announceStones(mixed);
    // A ring of one stone whose combination has no picture in the file (a design CATALOG has not drawn, 12609) shows the strip too, never
    // the page's own photo of another ring.
    const sameNoPicture = !mixed && !showHero && stoneAt3 >= 0 && chosen[stoneAt3] != null && !data.combos.some((c) => c.imageUrl && c.options[mAt] === chosen[mAt] && c.options[cAt] === chosen[cAt] && c.options[stoneAt3] === chosen[stoneAt3]);
    // A design with no picture drawn for it at all (71087, 71175: Stuller gave no stone names to draw) opens on the one sample photo
    // the page holds, with the strip of the stones beneath it and a line saying so; the first change of a stone leaves the strip alone.
    const noDrawn = !(data.hero && Object.keys(data.hero).length) && !(data.img && Object.keys(data.img).length);
    const showSample = !!mainImage && mixed && pristine && noDrawn;
    void showSample;
    // The beige stones card is the only place the chosen stones show on a design whose file draws nothing (71087, 71175); where the pictures change with the stones the dropdowns say them.
    const showStrip = noDrawn || sameNoPicture;
    // A change with no picture of its own yet holds the picture the shopper was looking at (the last good one, or the opening ring if nothing else has been painted), never the opening
    // ring put back in the frame. If the metal changed, that picture is the wrong metal: the frame goes soft and white until the new one is there.
    const curShown = composed && cmp.shown && cp && cmp.shown.key === cp.key;
    const waiting = !!mainImage && mixed && !pristine && !(drawnUrl && !!mainImage) && !curShown;
    if (!waiting) onScreen.metal = chosen[mAt];
    const box0 = anchor.parentElement;
    if (box0) box0.classList.toggle('djh-feed--hold', waiting && onScreen.metal !== chosen[mAt]);
    const failedNow = !!prm && failed.has(prm.key) && !drawn.has(prm.key);
    const below = noDrawn && !!mainImage && !sameNoPicture;
    anchor.hidden = showStrip && !below;
    strip.hidden = !showStrip;
    strip.classList.toggle('djh-feed__strip--below', below);
    if (strip) drawHint();
    // The photo's own box is a fixed square that clips what overflows it, so beneath the sample photo the strip sits after the box; in every other
    // state it takes the photo's place inside it.
    const box = anchor.parentElement;
    if (below && box) { if (strip.previousElementSibling !== box) box.insertAdjacentElement('afterend', strip); }
    else if (strip.previousElementSibling !== anchor) anchor.insertAdjacentElement('afterend', strip);
    preloadCompose();
    savePick();
    photoNote(failedNow);
    if (showStrip) {
      const square = data.shape === 'square';
      // replaceChildren turns a null argument into the text "null", so the optional note is left out of the list, never passed as null.
      // Its note line is always there under a photo, hidden when it has nothing to say, so saying it moves nothing.
      strip.replaceChildren(...[below ? el('p', { class: `djh-feed__strip-note${noDrawn ? '' : ' is-off'}` }, m.sampleNote || 'Shown with sample stones') : null,
        below ? null : el('p', { class: 'djh-feed__strip-title' }, 'Your stones, left to right'),
        el('div', { class: 'djh-feed__strip-dots' }, stoneApi.picks().map((v) => { const g = gemOf(v); return el('span', { class: 'djh-feed__strip-dot' }, el('i', { style: `background:${g ? g[2] : '#d9d6cd'};border-radius:${square ? '6px' : '50%'}` }), gemLabel(v)); }))].filter(Boolean));
    }
  }

  // The row under the photo for a ring with no angle set of its own: the same ring in each metal, at the pick's count and stones, from the
  // pictures the file already holds (the mixed opening ring, or the same-stone render). A tap chooses that metal, so the photo, the price
  // and the cart line can never disagree. It shows only where at least two metals have a picture; a changed mixed set has none.
  let metalThumbsFor = null;
  function paintMetalThumbs() {
    if (!thumbsBox || !stoneApi) return;
    const mAt = axes.findIndex((a) => a.metal);
    const cAt = axes.findIndex((a) => a.count);
    const sAt = axes.findIndex((a) => a.positions === 'count');
    const hide = () => { if (metalThumbsFor) thumbsBox.replaceChildren(); metalThumbsFor = null; thumbsBox.hidden = true; };
    if (mAt < 0 || cAt < 0 || sAt < 0 || chosen[cAt] == null) return hide();
    const mixed = stoneApi.mixed();
    const countAt = data.axes[cAt].values.findIndex((v) => v.value === chosen[cAt]);
    const urls = axes[mAt].values.map((metal, i) => {
      if (mixed) return stoneApi.pristine() && data.hero ? data.hero[`${i}|${countAt}`] || data.hero[`${metal}|${chosen[cAt]}`] || null : null;
      if (data.open === 'same' && stoneApi.pristine() && data.hero) return data.hero[`${i}|${countAt}`] || data.hero[`${metal}|${chosen[cAt]}`] || null;
      const c = chosen[sAt] != null && data.combos.find((x) => x.imageUrl && x.options[mAt] === metal && x.options[cAt] === chosen[cAt] && x.options[sAt] === chosen[sAt]);
      return c ? c.imageUrl : null;
    });
    if (urls.filter(Boolean).length < 2) return hide();
    const key = `${chosen[mAt]}|${urls.join('|')}`;
    if (metalThumbsFor !== key) {
      metalThumbsFor = key;
      const tap = (metal) => () => {
        const swatch = document.querySelector(`[data-axis="${mAt}"] .djh-product__swatch[data-value="${CSS.escape(metal)}"]`);
        if (swatch) swatch.click();
      };
      thumbsBox.replaceChildren(...axes[mAt].values.map((metal, i) => {
        if (!urls[i]) return null;
        const t = el('img', {
          class: `djh-product__thumb${metal === chosen[mAt] ? ' is-active' : ''}`, src: thumbSrc(urls[i]), 'data-djh-metal-thumb': metal, loading: 'lazy',
          alt: '', width: '72', height: '72', tabindex: '0', role: 'button', 'aria-label': fill(m.thumbMetal || 'View in N_METAL', { N_METAL: shown(metal) }),
        });
        t.addEventListener('click', tap(metal));
        t.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); tap(metal)(); } });
        return t;
      }));
    }
    thumbsBox.hidden = false;
  }

  function setButton(label, enabled) {
    if (!button) return;
    button.textContent = label;
    button.disabled = !enabled;
    updateSticky();
  }

  // The sticky buy bar (phones): price + the same button, shown only once a size is chosen (the main button is enabled), only
  // after the main button has scrolled out of view, and never while the gem sheet is open. The bar's button just presses the
  // main one, so every rule that button has still applies.
  let sticky = null; let mainVisible = true;
  function updateSticky() {
    if (!button || !sticky) return;
    const parts = String(button.textContent).split(' · ');
    sticky.querySelector('.djh-sticky__price').textContent = parts[1] || '';
    sticky.querySelector('.djh-sticky__btn').textContent = parts[0];
    // Before a size is chosen the bar reads "Choose a ring size" and takes the shopper to the size control (the signed 71816
    // mock); once the main button is live it presses that button.
    sticky.hidden = !(window.matchMedia('(max-width: 860px)').matches && !mainVisible && parts[0] && (!button.disabled || chosen.some((v) => v == null)) && !document.documentElement.classList.contains('djh-gl-lock'));
  }
  function initSticky() {
    if (!button || sticky || !('IntersectionObserver' in window)) return;
    sticky = el('div', { class: 'djh-sticky', hidden: true }, el('span', { class: 'djh-sticky__price', 'aria-hidden': 'true' }), el('button', { type: 'button', class: 'djh-sticky__btn' }));
    sticky.querySelector('.djh-sticky__btn').addEventListener('click', () => {
      if (!button.disabled) { button.click(); return; }
      // The listbox first: a comma selector would return the first-paint select, which comes earlier in the page and is hidden by now.
      const ask = document.querySelector('.djh-feed__sizesel .djh-gl__control') || document.querySelector('[data-djh-opening-size-select]');
      if (ask) { ask.scrollIntoView({ block: 'center', behavior: 'smooth' }); try { ask.focus({ preventScroll: true }); } catch (_e) { /* the scroll is enough */ } }
    });
    document.body.append(sticky);
    new IntersectionObserver((entries) => { mainVisible = entries[0].isIntersecting; updateSticky(); }).observe(button);
    new MutationObserver(updateSticky).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', updateSticky);
  }

  // One place that marks a choice "not made in this combination": the class that draws it, and what a screen reader hears. The
  // button stays tappable (the page explains what is made instead).
  function markNa(b, na) {
    b.classList.toggle('is-na', na);
    b.setAttribute('aria-disabled', na ? 'true' : 'false');
    // A listbox row says so in words (a hatch pattern is not read by a screen reader and is not drawn on a list row).
    const rowName = b.classList.contains('djh-gl__option') ? b.querySelector('.djh-gl__name') : null;
    if (rowName && !b.querySelector('.djh-gl__gem')) { rowName.textContent = na ? `${b.dataset.value} (${m.notAvailable || 'not available'})` : b.dataset.value; return; }
    const said = b.querySelector('.djh-feed__na-said');
    if (na && !said) { const sp = document.createElement('span'); sp.className = 'djh-visually-hidden djh-feed__na-said'; sp.textContent = ' (' + (m.notAvailable || 'not available') + ')'; b.appendChild(sp); }
    else if (!na && said) said.remove();
  }

  // The page arrives with Shopify's own first picture, which is not always the opening ring (a 3-stone picture over a 4-stone ring, a wrong lab ring that
  // corrected itself 1.6 s later). CSS keeps that picture out of sight until the picker has put the opening picture in the frame; a stylesheet timer shows
  // whatever is there after 3.5 s, so a picker that never starts leaves the page as it was.
  let opened = false;
  function markOpened() {
    if (opened) return;
    opened = true;
    const go = () => root.setAttribute('data-djh-opened', '');
    if (!mainImage || mainImage.complete) { go(); return; }
    mainImage.addEventListener('load', go, { once: true });
    mainImage.addEventListener('error', go, { once: true });
    setTimeout(go, 1500);
  }
  function paint() {
    initSticky();
    if (stoneApi) stoneApi.sync();
    if (openingAt === null) { openingAt = openingSeed || key(chosen); openingPrice = priceDisplay ? priceDisplay.textContent : null; }
    const atOpening = openingAt === key(chosen) && (!stoneApi || stoneApi.pristine());
    axes.forEach((axis, i) => {
      const group = axesBox.querySelector(`[data-axis="${i}"]`);
      if (axis.parted && !axis.fixed) { paintParted(axis, i, group); } else {
        // The several-stones axis (drawStoneRows) has no single chosen label; reading it as null threw on the first paint.
        const chosenEl = group.querySelector('[data-djh-feed-chosen]');
        if (chosenEl) chosenEl.textContent = chosen[i] == null ? m.pickOne : shown(chosen[i]);
      }
      const nat = group.querySelector('.djh-feed__gemsel select');
      if (nat && chosen[i] != null) nat.value = chosen[i];
      group.querySelectorAll('[data-gl]').forEach((g) => { if (g.__sync) g.__sync(); });
      group.querySelectorAll('[data-value]').forEach((b) => {
        const v = b.dataset.value;
        const on = chosen[i] === v;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.classList.toggle('is-selected', on);
        markNa(b, !available(i, v));
      });
      if (axis.split && !axis.fixed) paintSplit(axis, i, group);
      const note = group.querySelector('[data-djh-feed-note]');
      const n = noteFor(i);
      note.textContent = n.text;
      note.classList.toggle('djh-feed__note--warn', n.warn);
    });
    message = null;
    paintFinish();
    const complete = chosen.every((v) => v != null);
    const combo = complete ? byKey.get(key(chosen)) : null;
    // The photo follows the pick: a shelf variant's own photo, else the combination's render (combos[].imageUrl, a
    // mint), else the metal's shelf photo, else the photo the page opened with. Never a different design's picture.
    const metalAxis = axes.findIndex((a) => a.metal);
    const metalImage = metalAxis >= 0 && cfg.metalImages ? cfg.metalImages[chosen[metalAxis]] : null;
    // A partial pick shows the first offered combination it matches, so yellow + a square stone looks yellow and square.
    // Every combination carries its own render (combos[].imageUrl, CATALOG), shelf ones included, so the page keeps no
    // per-variant photo table of its own (it was 50 KB of every page and stopped at Liquid's 250 variants).
    const partial = !combo && touched ? data.combos.find((c) => (c.imageUrl || (Array.isArray(c.images) && c.images.length)) && chosen.every((v, j) => v == null || c.options[j] === v)) : null;
    // A chosen finish shows Stuller's photo of the ring finished that way, when the route sends one for the pick.
    const finished = finish && ((combo && combo.finishImages && combo.finishImages[finish]) || (partial && partial.finishImages && partial.finishImages[finish]));
    const angles = (combo && Array.isArray(combo.images) && combo.images.length && combo.images)
      || (partial && Array.isArray(partial.images) && partial.images.length && partial.images) || null;
    // The thumbnails are the pick's own angles and nothing else (Henry, 2026-10-02: the row stayed rose and oval while
    // the photo went yellow and marquise). The row the page arrives with belongs to Shopify's first variant, so it is
    // hidden until a pick brings its set. The angle the customer chose stays chosen: the next pick opens on the same
    // position in its own set, so "side view" stays the side view when the metal or stone changes.
    let angleAt = 0;
    if (thumbsBox) {
      const was = thumbsFor ? [...thumbsBox.querySelectorAll('[data-djh-thumb]')].findIndex((t) => t.classList.contains('is-active')) : -1;
      if (angles) {
        angleAt = Math.min(Math.max(was, 0), angles.length - 1);
        if (thumbsFor !== angles.join('|')) {
          thumbsFor = angles.join('|');
          thumbsBox.replaceChildren(...angles.map((url, n) => el('img', {
            class: `djh-product__thumb${n === angleAt ? ' is-active' : ''}`, src: thumbSrc(url), 'data-djh-thumb': true, 'data-full-src': url, loading: 'lazy',
            alt: '', width: '72', height: '72', tabindex: '0', role: 'button', 'aria-label': fill(m.thumb, { N_I: String(n + 1), N_T: String(angles.length) }),
          })));
        }
        thumbsBox.hidden = false;
      } else {
        thumbsFor = null;
        thumbsBox.hidden = true;
      }
    }
    // Until the customer changes a choice the page keeps the photo it arrived with: the card's picture (the opening ring is
    // the variant whose photo is the featured photo), so card → page is one continuous ring (Henry, 2026-10-03).
    // On a page with a stones picker the picker owns the picture of a mixed set: this line used to put the file's picture of the first matching combination (another stone's ring,
    // measured on QA: yellow-4-alexandrite.jpg 0.4 s after every change) or the metal's shelf photo in the frame, and the picker then replaced it: the "default picture" that popped up.
    const mixedNow = !!stoneApi && stoneApi.mixed();
    const photo = picked ? finished || (angles && angles[angleAt]) || (combo && combo.imageUrl) || (!mixedNow && partial && partial.imageUrl) || (stoneApi ? null : metalImage) : finished || null;
    if (mainImage && photo) {
      if (mainImage.getAttribute('src') !== photo) mainImage.src = photo;
      mainImage.removeAttribute('srcset');
    } else if (mainImage && shelfImage && !stoneApi && mainImage.getAttribute('src') !== shelfImage.src) {
      // (a page with a stones picker owns its picture: putting the page's own first photo back here was the "default picture" that popped up on every stone change)
      mainImage.src = shelfImage.src;
      if (shelfImage.srcset) mainImage.setAttribute('srcset', shelfImage.srcset);
    }
    if (stoneApi) {
      paintStonePhoto();
      if (!angles && !paintGallery()) { galOff(); paintMetalThumbs(); }
    }
    markOpened();
    draftNote.hidden = true;
    staffedNote.hidden = true;
    status.textContent = '';
    if (dynamicCheckout) dynamicCheckout.hidden = true;
    idInput.value = '';
    comboInput.value = complete ? doorKey(chosen) : '';
    // Our SKU for the pick (Henry, 2026-10-02: DJH-ST-…; the supplier's own number is staff-only): combos[].sku. A pick
    // with none (not made yet) shows no line rather than another pick's.
    const sku = combo ? combo.sku || null : null;
    if (skuLine && skuValue) {
      skuValue.textContent = sku || '';
      skuLine.hidden = !sku;
    }
    // leadDays: business days to the customer's door (Stuller's lead + the shop's delivery buffer, CATALOG). Null or
    // absent says nothing; the page never guesses one.
    if (leadLine) {
      let text = combo && Number.isFinite(combo.leadDays) ? fill(m.lead, { N_DAYS: String(combo.leadDays) }) : '';
      // A ring made to order from stones says it in weeks, as the signed page does ("about 2 to 3 weeks"), from djh.lead_weeks_min/max.
      if (stoneApi && Array.isArray(cfg.leadWeeks) && cfg.leadWeeks.length === 2) text = fill(m.leadWeeks || 'Made to order. Ready in about N_FROM to N_TO weeks.', { N_FROM: String(cfg.leadWeeks[0]), N_TO: String(cfg.leadWeeks[1]) });
      // Before a whole pick: the shortest to the longest lead among the combinations still open, so the page answers "when" before it is asked
      // (Henry, 2026-10-05). It is only ever built from combinations that carry a lead; if none does, it says nothing, as before.
      if (!text && !combo && m.leadRange) {
        let lo = Infinity;
        let hi = -Infinity;
        byKey.forEach((c, k) => {
          if (!Number.isFinite(c.leadDays)) return;
          const parts = k.split('|');
          if (!chosen.every((v, i) => v == null || parts[i] === v)) return;
          lo = Math.min(lo, c.leadDays);
          hi = Math.max(hi, c.leadDays);
        });
        if (Number.isFinite(lo) && Number.isFinite(hi)) text = lo === hi ? fill(m.lead, { N_DAYS: String(lo) }) : fill(m.leadRange, { N_FROM: String(lo), N_TO: String(hi) });
      }
      leadLine.textContent = text;
      leadLine.hidden = !leadLine.textContent;
    }
    paintDetails(combo, sku);
    // One read per whole pick, after the shopper settles (stepping through sizes asks only for the last one). It also
    // warms the engine the door reads, so a made-to-order "Add to bag" doesn't land on a cold one (Integration, 2026-10-03).
    clearTimeout(pickTimer);
    if (combo && combo.needsDetail) pickTimer = setTimeout(() => { const c = byKey.get(key(chosen)); if (c && c.needsDetail) loadPick(c); }, 350);
    // Different stones: no combination by design. "From" the lowest of the chosen stones' prices (beside the chosen metal, count and
    // size), and the main button is the custom-ring door (the stones, metal, count and size go with the request).
    if (stoneApi && stoneApi.mixed()) {
      const stoneAt2 = axes.findIndex((a) => a.positions === 'count');
      const on = stoneApi.picks();
      const fromMixed = data.combos.filter((c) => on.includes(c.options[stoneAt2]) && chosen.every((v, j) => j === stoneAt2 || v == null || c.options[j] === v)).map((c) => c.priceCents).filter(Number.isFinite);
      if (priceDisplay && atOpening && openingPrice != null) priceDisplay.textContent = openingPrice;
      else if (priceDisplay && fromMixed.length) priceDisplay.textContent = fill(m.from, { N_PRICE: money(Math.min(...fromMixed)) });
      idInput.value = '';
      setButton(customLabel(), true);
      return;
    }
    if (!combo) {
      const from = data.combos.filter((c) => chosen.every((v, i) => v == null || c.options[i] === v)).map((c) => c.priceCents).filter(Number.isFinite);
      // Until the customer changes a choice the page keeps the price it arrived with: the design's entry price (Shopify's price_min),
      // the same number its collection card shows (Integration, 2026-11-04). The offered set's lower made-to-order sizes
      // show as soon as the customer starts choosing, and coming back to the opening choices restores the arrival number.
      if (priceDisplay && atOpening && openingPrice != null) priceDisplay.textContent = openingPrice;
      else if (priceDisplay && from.length && picked) priceDisplay.textContent = fill(m.from, { N_PRICE: money(Math.min(...from)) });
      setButton(fill(m.choose, { N_AXES: axes.filter((_, i) => chosen[i] == null).map((a) => a.name.toLowerCase()).join(' and ') }), false);
      return;
    }
    // A stone-set ring the file holds no price for (the accent diamonds of 71817 and 71943 are not priced in yet): no number is shown, the
    // line says so, and the button is the custom ring. Never "$0.00".
    if (stoneApi && !Number.isFinite(combo.priceCents)) {
      staffedNote.textContent = m.staffed;
      staffedNote.hidden = false;
      setButton(customLabel(), true);
      return;
    }
    const addOn = finishOf(finish);
    const total = combo.priceCents + (addOn ? addOn.priceCents : 0);
    if (priceDisplay) priceDisplay.textContent = `${money(total)} ${m.plusTax}`;
    // Until the make door can price a stone set (CATALOG, 3 Nov), only a Shopify shelf variant adds to the cart; a quarter size or a
    // count the shelf does not carry is a custom ring (the exact price above stays; the stones, metal, count and size go with it).
    if (stoneApi && !(combo.sellable === 'live' && combo.variantId)) {
      staffedNote.textContent = 'This one is made to order with you. Choose Start a custom ring and we will confirm it by email, at this price.';
      staffedNote.hidden = false;
      setButton(customLabel(), true);
      return;
    }
    if (combo.sellable === 'staffed') {
      staffedNote.textContent = m.staffed;
      staffedNote.hidden = false;
      setButton(m.addToCart, false);
      return;
    }
    if (combo.sellable === 'draft') {
      draftNote.hidden = false; // C4: said BEFORE they choose it
      setButton(m.draftButton, true);
      return;
    }
    if (combo.sellable === 'live' && combo.variantId) {
      idInput.value = String(combo.variantId).replace(/\D/g, '');
      // Buy it now posts the ring alone; with a finish chosen it would drop the add-on line, so it stays hidden.
      if (dynamicCheckout) dynamicCheckout.hidden = !!addOn;
    }
    setButton(`${m.addToCart} · ${money(total)}`, true);
  }

  // Product Details, Brilliant Earth's two lists in our words (Henry, 2026-10-02): what the customer decides on, from the
  // pick and CATALOG's facts. A row with no value is not drawn; nothing is estimated (no per-stone carat, ever).
  // CATALOG's fact labels (contract §I) → where they show and what they read as. Stones is per finger size, so it only
  // arrives with a whole pick (combos[].facts), never before a size is chosen.
  const STONE_FACTS = { 'Total carat weight': 'Total carat weight', Clarity: 'Average clarity', Color: 'Average color', Stones: 'Number of stones', 'Stone size': 'Stone size', Origin: 'Origin', 'Stone type': 'Type' };
  // Product Details, refined (Henry approved the mockup, 2026-10-03): Ring and Diamond information side by side, a plain
  // word under each grade, and an "i" on the rows a customer may not know, opening a short explainer in our words.
  const COLOR_STEPS = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N–Z'];
  const CLARITY_STEPS = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'SI3', 'I1', 'I2', 'I3'];
  const colorWord = (v) => { const c = (String(v).match(/[D-Z]/) || [])[0]; if (!c) return ''; return c <= 'F' ? m.glossColorless : c <= 'J' ? m.glossNearColorless : m.glossFaint; };
  const clarityWord = (v) => { const t = (String(v).match(/FL|IF|VVS|VS|SI|I(?=\d|$|\s|–)/) || [])[0]; return { FL: m.glossFlawless, IF: m.glossFlawless, VVS: m.glossVvs, VS: m.glossVs, SI: m.glossSi, I: m.glossIncluded }[t] || ''; };
  const TIP_OF = { Width: 'wt', Thickness: 'wt', Setting: 'set', 'Metal weight': 'mw', Origin: 'origin', 'Total carat weight': 'ct', 'Average color': 'color', 'Average clarity': 'clarity' };
  let tipContext = {};
  function paintDetails(combo, sku) {
    if (!detailsBox) return;
    const ring = [];
    const stone = [];
    const row = (rows, label, value, gloss) => { if (!rows.some((r) => r.label.toLowerCase() === label.toLowerCase())) rows.push({ label, value, gloss: gloss || '' }); };
    if (sku) row(ring, m.style, sku);
    const f = finishOf(finish);
    if (f) row(ring, m.finish, f.name);
    let sizeChosen = null;
    axes.forEach((axis, i) => {
      const v = chosen[i];
      if (v == null) return;
      if (axis.split) { row(stone, axis.parts[0], axis.partOf.get(v)[0]); row(stone, m.stoneSize, fill(m.each, { N_SIZE: shown(axis.partOf.get(v)[1]) })); }
      else if (axis.parted) axis.parts.forEach((part, k) => row(ring, part, shown(axis.partOf.get(v)[k])));
      else if (/ring size/i.test(axis.name)) { sizeChosen = shown(v); row(ring, axis.name, sizeChosen, cfg.resizable === false ? m.glossNoResize : ''); }
      else row(ring, axis.name, shown(v));
    });
    const facts = [data.facts, data.details, combo && combo.facts].flatMap((list) => (Array.isArray(list) ? list : [])).filter((x) => x && x.label && x.value);
    const stones = (facts.find((x) => x.label === 'Stones') || {}).value;
    // A diamond's color, clarity and carat are not facts about a citrine: when the Stone axis offers a diamond among gems
    // (one product, one stone axis), those three rows show only for a diamond pick.
    const stoneAt = axes.findIndex((a) => !a.metal && /stone/i.test(a.name) && !/shape|size|count/i.test(a.name));
    const pickedStone = stoneAt >= 0 && chosen[stoneAt] != null ? String(chosen[stoneAt]) : '';
    const notDiamond = pickedStone !== '' && !/diamond/i.test(pickedStone);
    facts.forEach((x) => {
      const label = STONE_FACTS[x.label] || x.label;
      // CATALOG's `stones` key on a detail names the Stone values it applies to ("Natural Diamond"): show it only when the
      // current pick's Stone is one of them. With no key the rule above (a diamond's grade is not a citrine's) still holds.
      if (Array.isArray(x.stones) && x.stones.length) { if (pickedStone !== '' && !x.stones.includes(pickedStone)) return; }
      else if (notDiamond && ['Average color', 'Average clarity', 'Total carat weight'].includes(label)) return;
      // Stuller's own words are "Standard Prong"; under the label "Prong style" the second "Prong" is said twice.
      const value = x.label === 'Prong style' ? String(x.value).replace(/\s+prong$/i, '') : x.value;
      const gloss = label === 'Average color' ? colorWord(value) : label === 'Average clarity' ? clarityWord(value)
        : label === 'Total carat weight' && stones && sizeChosen ? fill(m.glossCt, { N_STONES: stones, N_SIZE: sizeChosen }) : '';
      row(STONE_FACTS[x.label] ? stone : ring, label, value, gloss);
    });
    const diamond = /diamond/i.test(cfg.title || '') || stone.some((r) => /diamond/i.test(r.value));
    const val = (rows, label) => (rows.find((r) => r.label === label) || {}).value;
    tipContext = { width: val(ring, 'Width'), thickness: val(ring, 'Thickness'), weight: val(ring, 'Metal weight'), ct: val(stone, 'Total carat weight'),
      color: val(stone, 'Average color'), clarity: val(stone, 'Average clarity'), origin: val(stone, 'Origin'), stones, size: sizeChosen,
      stoneSize: val(stone, m.stoneSize), metal: chosen[axes.findIndex((a) => a.metal)] || '', setting: val(ring, 'Setting') };
    const icon = (kind) => { const t = document.querySelector(`template[data-djh-details-icon="${kind}"]`); return t ? t.content.cloneNode(true) : null; };
    const block = (title, kind, rows) => (rows.length ? el('section', { class: 'djh-feed-details__block' },
      el('p', { class: 'djh-feed-details__head' }, icon(kind), title),
      el('dl', { class: 'djh-spec djh-spec--refined' }, rows.flatMap((r) => {
        const tip = TIP_OF[r.label] && (diamond || !['color', 'clarity', 'ct'].includes(TIP_OF[r.label])) ? TIP_OF[r.label] : null;
        return [
          el('dt', null, r.label, tip ? el('button', { type: 'button', class: 'djh-tip-btn', 'data-tip': tip, 'aria-haspopup': 'dialog', 'aria-expanded': 'false', 'aria-label': fill(m.tipAsk, { N_LABEL: r.label }) }) : null),
          el('dd', null, r.value, r.gloss ? el('span', { class: 'djh-spec__gloss' }, r.gloss) : null),
        ];
      }))) : null);
    detailsBox.replaceChildren(el('div', { class: 'djh-feed-details__cols' }, block(m.ringInfo, 'ring', ring), block(diamond ? m.diamondInfo : m.stoneInfo, 'gem', stone)), tipBox());
  }

  // The explainer: one dialog, filled from the row it was opened from; a panel beside the row on a wide screen, a sheet
  // from the bottom on a phone. Escape, the close button or a tap outside closes it, and focus returns to the "i".
  let tipEl = null;
  let tipFrom = null;
  function tipBox() {
    if (!tipEl) {
      tipEl = el('div', { class: 'djh-tip', role: 'dialog', 'aria-modal': 'false', 'aria-labelledby': 'DjhTipTitle', hidden: true });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !tipEl.hidden) closeTip(); });
      document.addEventListener('click', (e) => {
        const b = e.target.closest && e.target.closest('.djh-tip-btn');
        if (b) { e.preventDefault(); openTip(b); return; }
        if (!tipEl.hidden && !tipEl.contains(e.target)) closeTip();
      });
    }
    return tipEl;
  }
  function closeTip() {
    tipEl.hidden = true;
    document.documentElement.classList.remove('djh-tip-open');
    if (tipFrom) { tipFrom.setAttribute('aria-expanded', 'false'); tipFrom.focus(); tipFrom = null; }
  }
  function scaleArt(steps, value, groups) {
    const tokens = (String(value || '').match(/FL|IF|VVS[12]?|VS[12]?|SI[123]?|I[123]|[D-M]/g) || []);
    const idx = tokens.flatMap((t) => steps.map((s, i) => (s === t || (t.length <= 3 && /\d$/.test(s) && s.replace(/\d$/, '') === t) ? i : -1)).filter((i) => i >= 0));
    const lo = idx.length ? Math.min(...idx) : -1;
    const hi = idx.length ? Math.max(...idx) : -1;
    return el('div', { class: 'djh-tip__scale' },
      el('div', { class: 'djh-tip__steps' }, steps.map((st, i) => el('span', { class: i >= lo && i <= hi ? 'is-on' : null }, st))),
      groups ? el('div', { class: 'djh-tip__groups' }, groups.map(([t, n]) => el('span', { style: `flex:${n}` }, t))) : null);
  }
  function svg(markup) { const t = document.createElement('template'); t.innerHTML = markup; return t.content; }
  function openTip(btn) {
    const k = btn.dataset.tip;
    const c = tipContext;
    const esc = (x) => String(x == null ? '' : x).replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));
    const big = (a, b) => el('p', { class: 'djh-tip__big' }, a, b ? el('span', null, ` ${b}`) : null);
    const art = {
      clarity: () => scaleArt(CLARITY_STEPS, c.clarity, [[m.glossFlawless, 2], ['VVS', 2], ['VS', 2], [m.glossSi, 3], [m.glossIncluded, 3]]),
      color: () => scaleArt(COLOR_STEPS, c.color, [[m.glossColorless, 3], [m.glossNearColorless, 4], [m.glossFaint, 3], ['', 1]]),
      ct: () => (c.stones && c.stoneSize ? big(`${c.stones} × ${c.stoneSize.replace(/\s*each$/, '')}`, `= ${c.ct || ''}`) : big(c.ct || '')),
      mw: () => big(c.weight || '', [c.metal, c.size ? fill(m.tipSize, { N_SIZE: c.size }) : ''].filter(Boolean).join(', ')),
      wt: () => svg(`<svg class="djh-tip__art" viewBox="0 0 270 96" aria-hidden="true"><path d="M60 70 Q60 30 110 30 Q160 30 160 70 Z" fill="var(--djh-sand)" stroke="var(--djh-gold-text)"/><path d="M60 16H160M60 11V21M160 11V21" stroke="currentColor"/><text x="110" y="9" text-anchor="middle" font-size="11" fill="currentColor">${esc(fill(m.tipWidth, { N_VALUE: c.width || '' }))}</text><path d="M176 30V70M171 30H181M171 70H181" stroke="currentColor"/><text x="184" y="54" font-size="11" fill="currentColor">${esc(fill(m.tipThickness, { N_VALUE: c.thickness || '' }))}</text></svg>`),
      set: () => svg('<svg class="djh-tip__art" viewBox="0 0 200 66" aria-hidden="true"><rect x="8" y="24" width="184" height="18" rx="2" fill="var(--djh-sand)"/>' + [0, 1, 2, 3].map((i) => `<rect x="${24 + i * 44}" y="20" width="26" height="26" fill="#fff" stroke="var(--djh-stone-dark)"/>`).join('') + [0, 1, 2, 3, 4].map((i) => `<circle cx="${17 + i * 44}" cy="20" r="3.5" fill="var(--djh-gold-text)"/><circle cx="${17 + i * 44}" cy="46" r="3.5" fill="var(--djh-gold-text)"/>`).join('') + '</svg>'),
      origin: () => el('div', { class: 'djh-tip__pair' }, [m.originNatural, m.originLab].map((t) => el('span', { class: /lab/i.test(t) === /lab/i.test(c.origin || '') ? 'is-on' : null }, t))),
    }[k];
    const title = { wt: m.tipTitleWt, set: m.setting, mw: m.metalWeight, origin: m.origin, ct: m.carat, color: m.averageColor, clarity: m.averageClarity }[k] || '';
    const value = { set: c.setting, mw: c.weight, origin: c.origin, ct: c.ct, color: c.color, clarity: c.clarity }[k];
    const body = { wt: m.tipWt, set: m.tipSet, mw: m.tipMw, origin: m.tipOrigin, ct: m.tipCt, color: m.tipColor, clarity: m.tipClarity }[k] || '';
    const close = el('button', { type: 'button', class: 'djh-tip__close', 'aria-label': m.tipClose }, '×');
    close.addEventListener('click', closeTip);
    tipEl.replaceChildren(el('p', { class: 'djh-tip__title', id: 'DjhTipTitle' }, value ? `${title} · ${value}` : title), close, art ? art() : null, el('p', { class: 'djh-tip__body' }, body));
    if (tipFrom && tipFrom !== btn) tipFrom.setAttribute('aria-expanded', 'false');
    tipFrom = btn;
    btn.setAttribute('aria-expanded', 'true');
    tipEl.hidden = false;
    const phone = window.matchMedia('(max-width: 47.99rem)').matches;
    document.documentElement.classList.toggle('djh-tip-open', phone);
    if (phone) { tipEl.style.top = ''; tipEl.style.left = ''; } else {
      const box = detailsBox.getBoundingClientRect();
      const r = btn.getBoundingClientRect();
      tipEl.style.top = `${r.bottom - box.top + 8}px`;
      tipEl.style.left = `${Math.max(0, Math.min(r.left - box.left - 16, box.width - 360))}px`;
    }
    close.focus();
  }

  // The carat words for one stone value: the exact total for a whole pick (combos[].facts), else the value's range across
  // sizes and metals (axes[].values[].ctw, CATALOG 2814be3c4), one number when min = max. Absent says nothing.
  const factOf = (c, label) => (c && Array.isArray(c.facts) ? (c.facts.find((f) => f.label === label) || {}).value : null);
  function caratWords(axis, i, v) {
    const exact = chosen.every((c, j) => j === i || c != null) ? factOf(byKey.get(key(withValue(i, v))), 'Total carat weight') : null;
    if (exact) return { text: exact, exact: true };
    const r = axis.ctwOf.get(v);
    if (!r || !r.min) return { text: '', exact: false };
    return { text: r.min === r.max || !r.max ? r.min : `${r.min.replace(/ ct tw$/, '')}–${r.max}`, exact: false };
  }

  function drawSplitAxis(axis, i, pick) {
    const shapeId = `DjhFeedAxis${i}Shape`;
    const sizeId = `DjhFeedAxis${i}`;
    // The order a jeweler shows them (Round first), not the order the sizes sorted into; an unknown shape goes last.
    const SHAPES = ['round', 'oval', 'cushion', 'princess', 'square', 'emerald', 'radiant', 'asscher', 'pear', 'marquise', 'heart'];
    const rank = (shape) => { const r = SHAPES.indexOf(shape.toLowerCase()); return r < 0 ? 99 : r; };
    const shapes = [...new Set(axis.values.map((v) => axis.partOf.get(v)[0]))].sort((a, b) => rank(a) - rank(b));
    const choose = (shape) => () => {
      axis.shape = shape;
      const mine = axis.values.filter((v) => axis.partOf.get(v)[0] === shape);
      const was = chosen[i] == null ? null : axis.partOf.get(chosen[i])[1];
      const same = was == null ? null : mine.find((v) => axis.partOf.get(v)[1] === was && available(i, v));
      const ok = mine.filter((v) => available(i, v));
      const near = was == null ? ok[0] : ok.slice().sort((a, b) => Math.abs(num(axis.partOf.get(a)[1]) - num(was)) - Math.abs(num(axis.partOf.get(b)[1]) - num(was)))[0];
      pick(same || near || mine.find((v) => made(i, v)) || mine[0])();
    };
    const shapeRow = el('div', { class: 'djh-feed__shapes', role: 'group', 'aria-labelledby': shapeId },
      shapes.map((shape) => {
        const icon = document.querySelector(`template[data-djh-shape-icon="${shape.toLowerCase()}"]`);
        const b = el('button', { type: 'button', class: 'djh-feed__shape', 'data-shape': shape, 'aria-pressed': 'false' },
          el('span', { class: 'djh-feed__shape-icon', 'aria-hidden': 'true' }, icon ? icon.content.cloneNode(true) : null), el('span', null, shape));
        b.addEventListener('click', choose(shape));
        return b;
      }));
    const link = el('a', { class: 'djh-feed__shape-link', 'data-djh-feed-shape-link': true, hidden: true });
    const sizes = el('div', { class: 'djh-feed__chips', role: 'group', 'aria-labelledby': sizeId, 'data-axis-control': true },
      axis.values.map((v) => {
        const mm = shown(axis.partOf.get(v)[1]);
        const b = el('button', { type: 'button', class: `djh-feed__chip${axis.hasCtw ? ' djh-feed__chip--carat' : ''}`, 'data-value': v, 'data-shape': axis.partOf.get(v)[0], 'aria-pressed': 'false' },
          axis.hasCtw ? [el('span', { class: 'djh-feed__chip-main', 'data-djh-feed-ct': true }), el('span', { class: 'djh-feed__chip-sub' }, fill(m.stones, { N_SIZE: mm }))] : mm);
        b.addEventListener('click', pick(v));
        return b;
      }));
    return el('div', { class: 'djh-feed__axis', 'data-axis': i, 'data-split': true },
      el('p', { class: 'djh-feed__label', id: shapeId }, axis.parts[0], el('b', { 'data-djh-feed-shape-chosen': true }, m.pickOne)),
      shapeRow, link,
      el('p', { class: 'djh-feed__note', 'data-djh-feed-shape-first': true }, m.pickShape),
      el('p', { class: 'djh-feed__label', id: sizeId, 'data-djh-feed-size-head': true }, axis.hasCtw ? m.carat : m.stoneSize, el('b', { 'data-djh-feed-chosen': true }, m.pickOne)),
      sizes, el('p', { class: 'djh-feed__note', 'data-djh-feed-carat-note': true }),
      el('p', { class: 'djh-feed__note', 'data-djh-feed-note': true, 'aria-live': 'polite' }));
  }

  // The split axis between paints: which shape is showing, which shapes and sizes are made beside the other picks.
  function paintSplit(axis, i, group) {
    if (chosen[i] != null) axis.shape = axis.partOf.get(chosen[i])[0];
    const shape = axis.shape;
    group.querySelector('[data-djh-feed-shape-chosen]').textContent = shape || m.pickOne;
    const head = group.querySelector('[data-djh-feed-size-head]');
    head.hidden = !shape;
    group.querySelector('[data-djh-feed-shape-first]').hidden = !!shape;
    const cw = chosen[i] == null ? null : caratWords(axis, i, chosen[i]);
    group.querySelector('[data-djh-feed-chosen]').textContent = chosen[i] == null ? m.pickOne : axis.hasCtw && cw.text ? cw.text : shown(axis.partOf.get(chosen[i])[1]);
    if (axis.hasCtw) group.querySelectorAll('[data-djh-feed-ct]').forEach((n) => { n.textContent = caratWords(axis, i, n.parentNode.dataset.value).text; });
    const caratNote = group.querySelector('[data-djh-feed-carat-note]');
    const metalAt = axes.findIndex((a) => a.metal);
    const sizeAt = axes.findIndex((a, j) => j !== i && !a.metal);
    caratNote.textContent = !axis.hasCtw || !shape ? '' : cw && cw.exact && sizeAt >= 0
      ? fill(m.caratExact, { N_SIZE: shown(chosen[sizeAt]), N_METAL: metalAt >= 0 ? shown(chosen[metalAt]) : '' })
      : m.caratRange;
    caratNote.hidden = !caratNote.textContent;
    group.querySelectorAll('.djh-feed__shape').forEach((b) => {
      const on = b.dataset.shape === shape;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.classList.toggle('is-selected', on);
      markNa(b, !axis.values.some((v) => axis.partOf.get(v)[0] === b.dataset.shape && available(i, v)));
    });
    group.querySelectorAll('.djh-feed__chip').forEach((b) => { b.hidden = !shape || b.dataset.shape !== shape; });
    const link = group.querySelector('[data-djh-feed-shape-link]');
    if (shape && cfg.shapeFilterUrl) {
      link.href = cfg.shapeFilterUrl + encodeURIComponent(shape);
      link.textContent = fill(m.shapeLink, { N_SHAPE: shape });
      link.hidden = false;
    } else link.hidden = true;
  }

  // Weight, lightest to heaviest (checked against Stuller's grams: 4.0 g, 6.2 g, 9.9 g at 6 mm, size 10, 14K yellow).
  const WEIGHT_ORDER = ['ultra-lightweight', 'lightweight', 'standard weight', 'heavyweight'];
  const weightOrder = (list) => list.slice().sort((a, b) => {
    const x = WEIGHT_ORDER.indexOf(String(a).toLowerCase());
    const y = WEIGHT_ORDER.indexOf(String(b).toLowerCase());
    return (x < 0 ? 99 : x) - (y < 0 ? 99 : y);
  });
  // Design Detail, in Stuller's own order (None first), each with its cross-section drawn in the theme's line.
  const DETAIL_ORDER = ['none', 'stepped-edge', 'stepped-edge-milgrain', 'milgrain', 'double-milgrain', 'wide-milgrain', 'bead', 'rope', 'beveled-edge', 'grooved-round-edge'];
  const detailKey = (v) => String(v).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const detailOrder = (list) => list.slice().sort((a, b) => {
    const x = DETAIL_ORDER.indexOf(detailKey(a));
    const y = DETAIL_ORDER.indexOf(detailKey(b));
    return (x < 0 ? 99 : x) - (y < 0 ? 99 : y);
  });
  function detailGlyph(v) {
    const t = document.querySelector(`template[data-djh-glyph="${detailKey(v)}"]`);
    return t ? t.content.cloneNode(true) : null;
  }

  // A parted axis (Detail · Width · Weight): one chip row per part. Tapping a part keeps the other parts where they are
  // when that combination is made beside the other picks; else it moves them to the nearest made one, and says so.
  function drawPartedAxis(axis, i, pick) {
    const choosePart = (j, p) => () => {
      const want = axis.partPick.slice();
      want[j] = p;
      const parts = (v) => axis.partOf.get(v);
      const off = (v) => parts(v).reduce((s, q, k) => {
        if (k === j || want[k] == null || q === want[k]) return s;
        const d = Math.abs(num(q) - num(want[k]));
        return s + 1000 + (Number.isFinite(d) ? d : 0);
      }, 0);
      const mine = axis.values.filter((v) => parts(v)[j] === p);
      const ranked = (list) => list.slice().sort((a, b) => off(a) - off(b))[0];
      const v = ranked(mine.filter((x) => available(i, x))) || ranked(mine.filter((x) => made(i, x)));
      if (!v) {
        // No style with this part is made in the earlier picks (2.25 mm in 14K Yellow Gold, measured on the Half Round
        // Band 2026-10-03): the ring stays as it is and the page says what is made instead of clearing the style.
        const before = chosen.slice(0, i).filter((x) => x != null).map(shown).join(' · ');
        const alts = axis.partValues[j].filter((q) => axis.values.some((x) => parts(x)[j] === q && made(i, x)));
        const near = alts.slice().sort((a, b) => Math.abs(num(a) - num(p)) - Math.abs(num(b) - num(p)))[0];
        message = { axis: i, text: fill(m.closest, { N_PICK: before, N_AXIS: axis.parts[j].toLowerCase(), N_WAS: shown(p), N_NEAR: near == null ? '' : shown(near) }) };
        paint();
        return;
      }
      const moved = parts(v).findIndex((q, k) => k !== j && want[k] != null && q !== want[k]);
      // Said before settle runs, so a later pick that settle moves (the ring size) still gets its own note.
      if (moved >= 0) message = { axis: i, text: fill(m.moved, { N_PICK: shown(p), N_AXIS: axis.parts[moved].toLowerCase(), N_WAS: shown(want[moved]), N_NEAR: shown(parts(v)[moved]) }) };
      pick(v)();
    };
    const rows = axis.parts.map((part, j) => {
      const id = `DjhFeedAxis${i}Part${j}`;
      // Stuller's part is "Detail"; the shop says "Design Detail", and draws each one as the band's cross-section.
      const isDetail = /^detail$/i.test(part);
      const values = isDetail ? detailOrder(axis.partValues[j]) : /^weight$/i.test(part) ? weightOrder(axis.partValues[j]) : axis.partValues[j];
      const longest = values.reduce((n, p) => Math.max(n, shown(p).length), 0);
      return [
        el('p', { class: 'djh-feed__label', id }, isDetail ? m.partDetail : part, el('b', { 'data-djh-feed-part-chosen': j }, m.pickOne)),
        el('div', { class: `djh-feed__chips${isDetail ? ' djh-feed__chips--glyph' : longest > 7 ? ' djh-feed__chips--wide' : ''}`, role: 'group', 'aria-labelledby': id, 'data-axis-control': true },
          values.map((p) => {
            const glyph = isDetail ? detailGlyph(p) : null;
            const b = el('button', { type: 'button', class: `djh-feed__chip${glyph ? ' djh-feed__chip--glyph' : ''}`, 'data-part': j, 'data-part-value': p, 'aria-pressed': 'false' },
              glyph ? [glyph, el('span', null, shown(p))] : shown(p));
            b.addEventListener('click', choosePart(j, p));
            return b;
          })),
      ];
    });
    return el('div', { class: 'djh-feed__axis djh-feed__axis--parted', 'data-axis': i, 'data-parted': true },
      rows.flat(), el('p', { class: 'djh-feed__note', 'data-djh-feed-note': true, 'aria-live': 'polite' }));
  }

  // Which part values are chosen, and which are made beside the other picks (this axis's other parts included).
  function paintParted(axis, i, group) {
    if (chosen[i] != null) axis.partPick = axis.partOf.get(chosen[i]).slice();
    axis.parts.forEach((_, j) => {
      const label = group.querySelector(`[data-djh-feed-part-chosen="${j}"]`);
      if (label) label.textContent = axis.partPick[j] == null ? m.pickOne : shown(axis.partPick[j]);
    });
    group.querySelectorAll('[data-part]').forEach((b) => {
      const j = Number(b.dataset.part);
      const p = b.dataset.partValue;
      const on = axis.partPick[j] === p;
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.classList.toggle('is-selected', on);
      markNa(b, !axis.values.some((v) => {
        const q = axis.partOf.get(v);
        return q[j] === p && q.every((x, k) => k === j || axis.partPick[k] == null || x === axis.partPick[k]) && available(i, v);
      }));
    });
  }

  // A sibling axis (contract §I.3 fate `sibling`, e.g. the Half Round Band's Fit): each value is its own product, so the
  // switch is links, this product's value chosen. Drawn above the choices, where the Natural | Lab-Grown switch sits; an
  // axis the theme already drew from metafields (Origin) is not drawn twice.
  function drawSiblings() {
    const list = Array.isArray(data.siblings) ? data.siblings.filter((s) => s && s.axis && s.value && s.handle) : [];
    const byAxis = new Map();
    list.forEach((s) => byAxis.set(s.axis, [...(byAxis.get(s.axis) || []), s]));
    const root0 = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    byAxis.forEach((sibs, name) => {
      if (sibs.length < 2 || !sibs.some((s) => s.current)) return;
      if (root.querySelector(`[data-djh-sibling-axis="${name.toLowerCase()}"]`)) return;
      const current = sibs.find((s) => s.current);
      const id = `DjhFeedSibling${name.replace(/\W/g, '')}`;
      const box = el('div', { class: 'djh-feed__siblings', 'data-djh-sibling-axis': name.toLowerCase() },
        el('p', { class: 'djh-feed__label', id }, name, el('b', null, shown(current.value))),
        el('nav', { class: 'djh-origin-switch', 'aria-labelledby': id, style: `grid-template-columns: repeat(${Math.min(sibs.length, 4)}, 1fr)` },
          // Alphabetical by what is shown, the same on every page of the set (D-390): the page you are on is marked in place, never moved to the front.
          sibs.slice().sort((x, y) => shown(x.value).localeCompare(shown(y.value))).map((s) => (s.current
            ? el('span', { class: 'djh-origin-switch__option is-current', 'aria-current': 'page' }, shown(s.value))
            : el('a', { class: 'djh-origin-switch__option', href: `${root0}products/${encodeURIComponent(s.handle)}` }, shown(s.value))))));
      root.insertBefore(box, shelf);
      warmSiblings(sibs.filter((s) => !s.current).map((s) => `${root0}products/${encodeURIComponent(s.handle)}`));
    });
  }

  // The other fit, ready before the tap, the way the Natural ⇄ Lab switch is (djh-origin-sibling): prefetched once the
  // page is idle, prerendered on hover with a mouse and at once on a touch screen (Chrome on Android acts only on
  // 'immediate' there). A prerendered page fetches its own picker data. Browsers without Speculation Rules get a prefetch.
  function warmSiblings(urls) {
    if (!urls.length) return;
    const add = () => {
      if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
        const touch = window.matchMedia && window.matchMedia('(hover: none)').matches;
        const rules = document.createElement('script');
        rules.type = 'speculationrules';
        rules.textContent = JSON.stringify({ prefetch: [{ source: 'list', urls, eagerness: 'immediate' }], prerender: [{ source: 'list', urls, eagerness: touch ? 'immediate' : 'moderate' }] });
        document.head.appendChild(rules);
      } else {
        urls.forEach((href) => { const l = document.createElement('link'); l.rel = 'prefetch'; l.href = href; document.head.appendChild(l); });
      }
    };
    (window.requestIdleCallback || ((f) => setTimeout(f, 1500)))(add);
  }

  // The finish row, after the choices: Polished (included) and each finish the pick allows, as Stuller's swatch with its
  // price. Redrawn on every paint, because which finishes exist follows the picks.
  function paintFinish() {
    const box = axesBox.querySelector('[data-djh-feed-finish]');
    if (!box) return;
    const codes = finishCodes();
    if (finish && !codes.includes(finish)) {
      finishNote = fill(m.finishReset, { N_FINISH: finishOf(finish) ? finishOf(finish).name : finish });
      finish = null;
    }
    box.hidden = !codes.length;
    const choose = (code) => () => { finish = code; finishNote = ''; paint(); };
    const tile = (code, name, sub, swatch) => {
      const on = finish === code;
      const b = el('button', { type: 'button', class: `djh-feed__finish${on ? ' is-selected' : ''}`, 'aria-pressed': on ? 'true' : 'false', 'data-finish': code || 'polished' },
        el('span', { class: 'djh-feed__finish-swatch', 'aria-hidden': 'true' }, swatch ? el('img', { src: swatch, alt: '', loading: 'lazy', width: '56', height: '56' }) : null),
        el('span', { class: 'djh-feed__finish-name' }, name), el('span', { class: 'djh-feed__finish-price' }, sub));
      b.addEventListener('click', choose(code));
      return b;
    };
    const chosenF = finishOf(finish);
    box.querySelector('[data-djh-feed-finish-chosen]').textContent = chosenF ? chosenF.name : m.finishPolished;
    box.querySelector('[data-djh-feed-finishes]').replaceChildren(
      tile(null, m.finishPolished, m.finishIncluded, null),
      ...codes.map((code) => { const f = finishOf(code); return tile(code, f.name, fill(m.finishPrice, { N_PRICE: money(f.priceCents) }), f.swatchUrl || null); }));
    const note = box.querySelector('[data-djh-feed-finish-note]');
    note.textContent = finishNote || (chosenF ? fill(m.finishNote, { N_FINISH: chosenF.name, N_PRICE: money(chosenF.priceCents) }) : '');
    note.classList.toggle('djh-feed__note--warn', !!finishNote);
    finishNote = '';
  }
  function drawFinish() {
    if (!Array.isArray(data.finishes) || !data.finishes.length) return null;
    return el('div', { class: 'djh-feed__axis', 'data-djh-feed-finish': true, hidden: true },
      el('p', { class: 'djh-feed__label', id: 'DjhFeedFinish' }, m.finish, el('b', { 'data-djh-feed-finish-chosen': true }, m.finishPolished)),
      el('div', { class: 'djh-feed__finishes', role: 'group', 'aria-labelledby': 'DjhFeedFinish', 'data-djh-feed-finishes': true }),
      el('p', { class: 'djh-feed__note', 'data-djh-feed-finish-note': true, 'aria-live': 'polite' }));
  }

  // THE GEM LISTBOX (Henry, 2026-11-02: option A, "A renders the best on mobile"; a native <select> opens the phone's own
  // text-only picker, so the gems cannot show there). One control for the Stone axis on every family page:
  // the closed control is a gem + name + month + caret (44 px high); a tap opens the list as a bottom sheet on a phone
  // (<= 860 px) and a panel on a desktop. The native <select> stays in the DOM, visually hidden, as the form's source of
  // truth: choosing a row sets its value and dispatches `change`, so the picks and the add button keep working.
  // The gems are inline SVG from the one GEMS table above, each with its own gradient id (a shared id collapses on some
  // WebKit builds), sized in px, aria-hidden. A round CSS dot sits behind each so a failed SVG still shows the color.
  let glSeq = 0;
  const hexes = (c) => String(c).match(/#[0-9a-f]{6}/gi) || [];
  // A ring set with square stones (71175, 71943: the file says shape "square") draws its stone icons square.
  let squareStones = false;
  function gemSvg(value, size) {
    const g = gemOf(value);
    const id = `djhgl${++glSeq}`;
    const colors = g ? hexes(g[2]) : [];
    const base = colors[0] || '#d9d6cd';
    const fill = colors.length > 1
      ? `<linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1"><stop offset="0.5" stop-color="${colors[0]}"/><stop offset="0.5" stop-color="${colors[1]}"/></linearGradient>`
      : `<radialGradient id="${id}" cx="32%" cy="28%" r="75%"><stop offset="0" stop-color="#ffffff" stop-opacity="0.9"/><stop offset="0.2" stop-color="${base}"/><stop offset="1" stop-color="${base}"/></radialGradient>`;
    const span = document.createElement('span');
    span.className = 'djh-gl__gem';
    span.style.setProperty('--gem', base);
    if (squareStones) { span.style.borderRadius = '5px'; span.style.background = 'none'; }
    span.style.width = `${size}px`;
    span.style.height = `${size}px`;
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML = `<svg viewBox="0 0 40 40" width="${size}" height="${size}" focusable="false"><defs>${fill}</defs>${squareStones ? `<rect x="5" y="5" width="30" height="30" rx="3" fill="url(#${id})" stroke="rgba(0,0,0,0.35)" stroke-width="0.9"/>` : `<circle cx="20" cy="20" r="17" fill="url(#${id})" stroke="rgba(0,0,0,0.35)" stroke-width="0.9"/>`}<g fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="0.6" stroke-linejoin="round">${squareStones ? `<rect x="12" y="12" width="16" height="16"/><path d="M5 5L12 12M35 5L28 12M35 35L28 28M5 35L12 28"/>` : `<polygon points="28,16.7 28,23.3 23.3,28 16.7,28 12,23.3 12,16.7 16.7,12 23.3,12"/><path d="M28 16.7L35.7 13.5M28 23.3L35.7 26.5M23.3 28L26.5 35.7M16.7 28L13.5 35.7M12 23.3L4.3 26.5M12 16.7L4.3 13.5M16.7 12L13.5 4.3M23.3 12L26.5 4.3"/>`}</g></svg>`;
    return span;
  }

  // One menu open at a time, on the whole page (Henry, 2026-11-04: "when you click drop down, it stays down"; two menus stood open over each other). The open one is remembered here;
  // opening another closes it, and so do a press anywhere outside, Escape, Tab, focus moving elsewhere, choosing, and a second press on its own button.
  let openGl = null;
  // opts: { id, select (the native select that is the source of truth), values (in order), labelledby, chooseText, onPick(v) }
  function gemListbox(opts) {
    const { id, select, values, labelledby, chooseText, onPick, plain } = opts;
    const lab = (v) => (plain ? String(v) : gemLabel(v));
    const phone = () => window.matchMedia('(max-width: 860px)').matches;
    const control = el('button', { type: 'button', class: 'djh-gl__control', id: `${id}Ctl`, role: 'combobox', 'aria-haspopup': 'listbox', 'aria-expanded': 'false', 'aria-controls': `${id}List`, 'aria-labelledby': `${labelledby} ${id}Ctl` });
    const scrim = el('div', { class: 'djh-gl__scrim', hidden: true });
    const panel = el('div', { class: 'djh-gl__panel', id: `${id}List`, role: 'listbox', tabindex: '-1', 'aria-labelledby': labelledby, hidden: true });
    const rows = values.map((v, k) => {
      const g = plain ? null : gemOf(v);
      const row = el('div', { class: 'djh-gl__option', role: 'option', id: `${id}Opt${k}`, 'data-value': v, 'aria-selected': 'false', 'aria-pressed': 'false' },
        ...(plain ? [el('span', { class: 'djh-gl__name' }, lab(v))] : [gemSvg(v, 28), el('span', { class: 'djh-gl__name' }, gemLabel(v)), el('span', { class: 'djh-gl__month' }, g ? g[1] : '')]));
      // A ring size that is not made stays in the list, marked, and cannot be chosen (Henry, 2 Nov: unavailable sizes are
      // shown, not hidden); the note under the control says which are made.
      row.addEventListener('click', () => { if (plain && row.classList.contains('is-na')) return; choose(v); });
      return row;
    });
    panel.append(...rows);
    const root = el('div', { class: 'djh-gl', 'data-gl': true }, control, scrim, panel);
    let active = -1; let typed = ''; let typedAt = 0; let isOpen = false;

    const current = () => (select ? select.value : null);
    function sync() {
      const v = current();
      const g = v && !plain ? gemOf(v) : null;
      control.replaceChildren(...(plain
        ? [el('span', { class: 'djh-gl__name' }, v ? lab(v) : chooseText), el('span', { class: 'djh-gl__caret', 'aria-hidden': 'true' })]
        : [
          g || v ? gemSvg(v, 28) : (() => { const d = el('span', { class: 'djh-gl__gem djh-gl__gem--none', 'aria-hidden': 'true' }); return d; })(),
          el('span', { class: 'djh-gl__name' }, v ? gemLabel(v) : chooseText),
          el('span', { class: 'djh-gl__month' }, g ? g[1] : ''),
          el('span', { class: 'djh-gl__caret', 'aria-hidden': 'true' }),
        ]));
      rows.forEach((r) => { const on = v != null && r.dataset.value === v; r.setAttribute('aria-selected', on ? 'true' : 'false'); r.classList.toggle('is-selected', on); });
    }
    function setActive(k) {
      active = Math.max(0, Math.min(rows.length - 1, k));
      rows.forEach((r, j) => r.classList.toggle('is-active', j === active));
      control.setAttribute('aria-activedescendant', rows[active].id);
      rows[active].scrollIntoView({ block: 'nearest' });
    }
    const self = { close: (f) => close(f) };
    function open() {
      if (isOpen) return;
      if (openGl && openGl !== self) openGl.close(false);
      openGl = self;
      isOpen = true;
      panel.hidden = false;
      // On a screen with room the list drops below its button; with no room below it flips above, so it never covers the next field.
      panel.classList.remove('is-up');
      if (!phone()) {
        const r = control.getBoundingClientRect();
        const need = Math.min(panel.scrollHeight, 320) + 8;
        if (window.innerHeight - r.bottom < need && r.top > window.innerHeight - r.bottom) panel.classList.add('is-up');
      }
      control.setAttribute('aria-expanded', 'true');
      if (phone()) { scrim.hidden = false; document.documentElement.classList.add('djh-gl-lock'); }
      const k = rows.findIndex((r) => r.dataset.value === current());
      setActive(k < 0 ? 0 : k);
    }
    function close(returnFocus) {
      if (!isOpen) return;
      if (openGl === self) openGl = null;
      isOpen = false;
      panel.hidden = true;
      scrim.hidden = true;
      document.documentElement.classList.remove('djh-gl-lock');
      control.setAttribute('aria-expanded', 'false');
      control.removeAttribute('aria-activedescendant');
      if (returnFocus) control.focus();
    }
    function choose(v) {
      if (select) { select.value = v; select.dispatchEvent(new Event('change')); }
      if (onPick) onPick(v);
      sync();
      close(true);
    }
    control.addEventListener('click', () => { if (isOpen) close(true); else open(); });
    scrim.addEventListener('click', () => close(true));
    control.addEventListener('keydown', (e) => {
      const k = e.key;
      if (!isOpen && (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ')) { e.preventDefault(); open(); return; }
      if (!isOpen) return;
      if (k === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (k === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (k === 'Home') { e.preventDefault(); setActive(0); }
      else if (k === 'End') { e.preventDefault(); setActive(rows.length - 1); }
      else if (k === 'Enter' || k === ' ') { e.preventDefault(); if (!(plain && rows[active].classList.contains('is-na'))) choose(rows[active].dataset.value); }
      else if (k === 'Escape') { e.preventDefault(); close(true); }
      else if (k === 'Tab') { close(false); }
      else if (k.length === 1) {
        const now = Date.now(); typed = now - typedAt > 700 ? k.toLowerCase() : typed + k.toLowerCase(); typedAt = now;
        const hit = rows.findIndex((r) => lab(r.dataset.value).toLowerCase().startsWith(typed));
        if (hit >= 0) setActive(hit);
      }
    });
    document.addEventListener('keydown', (e) => { if (isOpen && e.key === 'Escape' && document.activeElement !== control) close(true); });
    // A press anywhere outside the menu (its button, list and scrim) closes it; so does focus moving to something else.
    document.addEventListener('pointerdown', (e) => { if (isOpen && !root.contains(e.target)) close(false); }, true);
    document.addEventListener('focusin', (e) => { if (isOpen && !root.contains(e.target)) close(false); });
    if (select) select.addEventListener('change', sync);
    root.__sync = sync;
    root.__rows = rows;
    sync();
    return root;
  }

  // A ring with several stones (family rings): one dropdown per stone, the way Stuller lists "Stone Locations" row by row,
  // so a shopper never meets one wall of 12 chips per stone. axis.positions is how many (a number), or 'count' when the ring's
  // own Count axis says (71816, 3 Nov): the rows then follow the count. Every stone the same is one combination (the Stone
  // pick is that stone: priced, in the cart). Different stones have no row in the file by design: the price is "From" the
  // lowest of the chosen stones, and the ring goes to the custom-ring door with its metal, count and size.
  const customLabel = () => m.customRing || 'Start a custom ring';
  const stoneText = (v) => {
    const origin = /lab-grown/i.test(window.location.pathname) ? 'Lab-grown' : 'Natural';
    // A value that already names its origin ("Lab-Grown Ruby") is sent as it is; only a bare stone name takes the page's origin.
    return v ? (/^(natural|lab)/i.test(String(v)) ? shown(v) : `${origin} ${gemLabel(v)}`) : 'not chosen yet';
  };
  function goCustom(stones, extra) {
    const handleMatch = window.location.pathname.match(/\/products\/([a-z0-9-]+)/);
    if (!handleMatch) return;
    try { sessionStorage.setItem('djh-mto-stones:' + handleMatch[1], JSON.stringify({ stones: stones.map(stoneText), ...(extra || {}) })); } catch (_e) { /* the form still opens */ }
    window.location.href = '/pages/made-to-order?from=' + handleMatch[1];
  }
  // The default mixed set: one birthstone for each person (Henry, 3 Nov: the page starts mixed, "various birthstones").
  const START_STONES = ['Ruby', 'Citrine', 'Aquamarine', 'Amethyst', 'Peridot', 'Blue Sapphire', 'Emerald', 'Pink Tourmaline', 'Amethyst', 'Garnet', 'Alexandrite', 'Diamond'];
  const startStones = (values, n) => {
    const by = (name) => values.find((v) => gemLabel(v).toLowerCase() === name.toLowerCase()) || null;
    // The file may name the opening stones in the order its opening pictures were drawn in (startStones: ["Blue Sapphire", "Emerald", ...]), so the
    // strip and the picture can never disagree; without it the page's own order stands.
    const named = data && Array.isArray(data.startStones) && data.startStones.length ? data.startStones : START_STONES;
    const seq = named.map(by).filter(Boolean);
    const pool = seq.length ? seq : values;
    // A design whose picture server draws one stone type for every place (71175) opens on the first stone in every place, so its opening
    // picture and the strip agree; a mix of it cannot be drawn.
    if (data && data.open === 'same') return Array.from({ length: n }, () => pool[0]);
    return Array.from({ length: n }, (_, k) => pool[k % pool.length]);
  };
  let stoneApi = null;
  function drawStoneRows(axis, i, labelId) {
    const dynamic = axis.positions === 'count';
    const countAt = dynamic ? axes.findIndex((a) => a.count) : -1;
    const nNow = () => (dynamic ? (Number(chosen[countAt]) || 0) : axis.positions);
    const values = axis.values.slice().sort((x, y) => gemRank(x) - gemRank(y));
    let picks = dynamic && startPicks ? startPicks.slice() : [];
    const head = el('p', { class: 'djh-feed__label', id: labelId }, 'Birthstones', el('b', { 'data-djh-feed-stones-count': true }));
    const rows = el('div', { class: 'djh-feed__stones', role: 'group', 'aria-labelledby': labelId, 'data-axis-control': true });
    const selects = [];
    const mixedNow = () => picks.length > 1 && picks.every(Boolean) && new Set(picks).size > 1;
    const sameNow = () => picks.length > 0 && picks.every(Boolean) && new Set(picks).size === 1;
    const noteOf = () => {
      const notes = (data && data.stones && typeof data.stones === 'object') ? data.stones : {};
      return [...new Set(picks.filter(Boolean))].map((v) => notes[v] || notes[gemLabel(v)]).filter(Boolean).join(' ');
    };
    const mixedPrompt = el('p', { class: 'djh-feed__mixprompt', hidden: true }, 'We started you with a mix of birthstones, one for each person. Change any stone to make it yours. A mixed ring is designed with you: choose Start a custom ring and your stones, count, metal and size go with it. Or choose Use the first stone for all to buy it now at a known price.');
    const stoneNote = el('p', { class: 'djh-feed__note djh-feed__note--stone', 'data-djh-feed-stone-note': true, hidden: true });
    const count = () => {
      const n = selects.length;
      const done = selects.filter((x) => x.value).length;
      head.querySelector('[data-djh-feed-stones-count]').textContent = fill(m.stonesCount || 'N_DONE of N_ALL chosen', { N_DONE: String(done), N_ALL: String(n) });
      all.hidden = !(selects[0] && selects[0].value) || (done === n && sameNow());
      if (custom) custom.disabled = done === 0;
      const note = noteOf();
      stoneNote.textContent = note;
      stoneNote.hidden = !note;
      mixedPrompt.hidden = !mixedNow();
    };
    function build(n) {
      rows.replaceChildren();
      selects.length = 0;
      for (let k = 0; k < n; k++) {
        const rowLabelId = `${labelId}Row${k}`;
        const sel = el('select', { class: 'djh-visually-hidden', tabindex: '-1', 'aria-hidden': 'true', id: `${labelId}Stone${k}`, 'aria-label': `Stone ${k + 1} of ${n}` },
          el('option', { value: '' }, 'Choose a stone'),
          values.map((v) => el('option', { value: v }, `${gemLabel(v)} · ${(gemOf(v) || [])[1] || ''}`)));
        sel.value = picks[k] || '';
        sel.addEventListener('change', () => { if (dynamic) onRow(); else count(); });
        selects.push(sel);
        const box = gemListbox({ id: `${labelId}Gl${k}`, select: sel, values, labelledby: rowLabelId, chooseText: 'Choose a stone' });
        rows.append(el('div', { class: 'djh-feed__stone-row' }, el('span', { class: 'djh-feed__stone-label', id: rowLabelId }, `Stone ${k + 1} of ${n}`), el('div', { class: 'djh-feed__stone-pick' }, sel, box)));
      }
      count();
    }
    // The rows follow the Count axis: a new place takes the first stone (the mixed set stays mixed), a dropped one is gone.
    function onRow() {
      picks = selects.map((x) => x.value || null);
      chosen[i] = sameNow() ? picks[0] : null;
      touched = true; picked = true;
      settle(i, sameNow() ? i : null);
      paint();
    }
    const all = el('button', { type: 'button', class: 'djh-feed__link', hidden: true }, 'Use the first stone for all');
    const handleMatch = window.location.pathname.match(/\/products\/([a-z0-9-]+)/);
    // The prototype's own button (a fixed number of stones); a count-driven ring uses the main button instead.
    const custom = !dynamic && handleMatch ? el('button', { type: 'button', class: 'djh-feed__custom' }, customLabel()) : null;
    if (custom) {
      custom.addEventListener('click', () => goCustom(selects.map((x) => x.value || null)));
      custom.disabled = true;
    }
    all.addEventListener('click', () => { selects.forEach((x) => { x.value = selects[0].value; }); if (dynamic) onRow(); else selects.forEach((x) => x.dispatchEvent(new Event('change'))); });
    if (dynamic) {
      stoneApi = {
        sync() {
          const n = nNow();
          if (n === selects.length) return;
          // An untouched opening set grows and shrinks along the opening sequence, so the family-idea picture for the new count still matches the
          // strip; a set the shopper has changed keeps what they chose and a new place takes the first stone.
          const untouchedSet = picks.join('|') === startStones(values, picks.length).join('|');
          const keep = untouchedSet ? startStones(values, n) : picks.slice(0, n);
          while (keep.length < n) keep.push(keep[0] || startStones(values, 1)[0]);
          picks = keep;
          chosen[i] = sameNow() ? picks[0] : null;
          build(n);
        },
        mixed: mixedNow,
        picks: () => picks.slice(),
        pristine: () => picks.join('|') === startStones(values, picks.length).join('|'),
        opening: () => startStones(values, picks.length),
        values,
      };
      build(nNow());
    } else {
      build(axis.positions);
    }
    return el('div', { class: 'djh-feed__axis', 'data-axis': i }, head, mixedPrompt, rows, all, stoneNote, custom, el('p', { class: 'djh-feed__note', 'data-djh-feed-note': true }));
  }

  // The Stone axis of a one-stone family page: the gem listbox over a hidden native select (the source of truth). Choosing a
  // row changes the select, which tells the picker the same way a chip tap did.
  function gemSelect(axis, i, labelId, pick) {
    const values = axis.values.slice().sort((x, y) => gemRank(x) - gemRank(y));
    const sel = el('select', { class: 'djh-visually-hidden', tabindex: '-1', 'aria-hidden': 'true', id: `${labelId}Native`, 'aria-label': 'Birthstone' },
      el('option', { value: '' }, m.pickOne), values.map((v) => el('option', { value: v }, shown(v))));
    sel.addEventListener('change', () => { if (sel.value) pick(sel.value)(); });
    const box = gemListbox({ id: `${labelId}Gl`, select: sel, values, labelledby: labelId, chooseText: m.pickOne });
    return el('div', { class: 'djh-feed__gemsel', 'data-axis-control': true }, sel, box);
  }

  // The ring size: the same listbox as the stone rows, plain, over a hidden native select. A size that is not made shows in the
  // list as "(not available)" and cannot be chosen; the line under the control (noteFor) says which sizes are made.
  function sizeSelect(axis, i, labelId, pick) {
    const sel = el('select', { class: 'djh-visually-hidden', tabindex: '-1', 'aria-hidden': 'true', id: `${labelId}Native`, 'aria-label': axis.name },
      el('option', { value: '' }, m.pickOne), axis.values.map((v) => el('option', { value: v }, shown(v))));
    sel.addEventListener('change', () => { if (sel.value) pick(sel.value)(); });
    const box = gemListbox({ id: `${labelId}Gl`, select: sel, values: axis.values, labelledby: labelId, chooseText: m.pickOne, plain: true });
    return el('div', { class: 'djh-feed__gemsel djh-feed__sizesel', 'data-axis-control': true }, sel, box);
  }

  function drawAxis(axis, i) {
    const labelId = `DjhFeedAxis${i}`;
    const gemAxis = !axis.metal && !axis.split && !axis.parted && isGemAxis(axis);
    const head = el('p', { class: 'djh-feed__label', id: labelId }, gemAxis ? 'Birthstone' : axis.count ? 'How many stones' : axis.name, el('b', { 'data-djh-feed-chosen': true }, m.pickOne));
    const pick = (v) => () => {
      chosen[i] = v;
      touched = true;
      picked = true;
      settle(i, axis.metal ? null : i);
      paint();
    };
    if (axis.fixed) {
      return el('div', { class: 'djh-feed__axis djh-feed__axis--fixed', 'data-axis': i },
        el('p', { class: 'djh-feed__label', id: labelId }, axis.name, el('b', { 'data-djh-feed-chosen': true }, shown(axis.values[0]))),
        el('p', { class: 'djh-feed__note', 'data-djh-feed-note': true, 'aria-live': 'polite' }));
    }
    if (gemAxis && (axis.positions === 'count' || axis.positions > 1)) return drawStoneRows(axis, i, labelId);
    if (axis.split) return drawSplitAxis(axis, i, pick);
    if (axis.parted) return drawPartedAxis(axis, i, pick);
    const control = axis.metal
      ? el('div', { class: 'djh-product__swatches djh-feed__swatches', role: 'group', 'aria-labelledby': labelId, 'data-axis-control': true },
        axis.values.map((v) => {
          const b = el('button', { type: 'button', class: `djh-product__swatch djh-product__swatch--${tone(v)}`, 'data-value': v, 'aria-pressed': 'false', title: v },
            el('span', { class: 'djh-product__swatch-dot', 'aria-hidden': 'true' }), el('span', { class: 'djh-visually-hidden' }, v));
          b.addEventListener('click', pick(v));
          return b;
        }))
      : gemAxis ? gemSelect(axis, i, labelId, pick)
      : /ring size/i.test(axis.name) ? sizeSelect(axis, i, labelId, pick)
      : el('div', { class: `djh-feed__chips${gemAxis ? ' djh-feed__chips--gem' : ''}${/ring size/i.test(axis.name) ? ' djh-feed__chips--size' : ''}${axis.count ? ' djh-feed__chips--count' : ''}`, role: 'group', 'aria-labelledby': labelId, 'data-axis-control': true },
        (gemAxis ? axis.values.slice().sort((x, y) => gemRank(x) - gemRank(y)) : axis.values).map((v) => {
          const gem = gemAxis ? gemOf(v) : null;
          const b = gem
            ? el('button', { type: 'button', class: 'djh-feed__chip djh-feed__chip--gem', 'data-value': v, 'aria-pressed': 'false', title: shown(v), style: `--gem:${gem[2]}` },
              el('span', { class: 'djh-feed__gem-dot', 'aria-hidden': 'true' }), el('span', { class: 'djh-feed__gem-name' }, gemLabel(v)), el('span', { class: 'djh-feed__gem-month' }, gem[1]))
            : el('button', { type: 'button', class: 'djh-feed__chip', 'data-value': v, 'aria-pressed': 'false' }, shown(v));
          b.addEventListener('click', pick(v));
          return b;
        }));
    return el('div', { class: 'djh-feed__axis', 'data-axis': i }, head, control, el('p', { class: 'djh-feed__note', 'data-djh-feed-note': true, 'aria-live': 'polite' }));
  }

  async function make(combo) {
    let res;
    try {
      res = await fetch(cfg.makeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        // The finish rides along only when one is chosen, so a priced draft (C4) can include it.
        body: JSON.stringify({ productId: String(cfg.productId), options: doorOptions(combo.options), ...(finish ? { finish } : {}) }),
      });
    } catch (_e) {
      return { down: true };
    }
    const body = await res.json().catch(() => null);
    if (res.status === 422 && body && body.error) return { error: body.error };
    if (!res.ok || !body || (body.kind !== 'variant' && body.kind !== 'draft')) return { down: true };
    return body;
  }

  async function onSubmit(event) {
    const shelfPick = !!(stoneApi && chosen.every((v) => v != null) && (() => { const c = byKey.get(key(chosen)); return c && c.sellable === 'live' && c.variantId; })());
    if (stoneApi && (stoneApi.mixed() || !shelfPick)) {
      event.preventDefault();
      const mAt = axes.findIndex((a) => a.metal);
      const cAt = axes.findIndex((a) => a.count);
      const zAt = axes.findIndex((a) => /ring size/i.test(a.name));
      const sAt = axes.findIndex((a) => a.positions === 'count');
      const one = chosen.every((v) => v != null) ? byKey.get(key(chosen)) : null;
      goCustom(stoneApi.picks(), {
        metal: mAt >= 0 ? chosen[mAt] : null, count: cAt >= 0 ? chosen[cAt] : null, size: zAt >= 0 ? chosen[zAt] : null,
        // The contract with OPS (CATALOG, 3 Nov): canonical stone keys in place order, the design the file names, and the price the
        // page showed when it showed one (a ring of one stone: its exact price; a mix: none, "From" is not a quote) with the moment.
        stoneKeys: stoneApi.picks().map((v) => (sAt >= 0 && axes[sAt].keyOf.get(v)) || null),
        designKey: typeof data.design === 'string' ? data.design : null,
        shownPriceCents: one && Number.isFinite(one.priceCents) ? one.priceCents : null,
        shownAt: Date.now(),
      });
      return;
    }
    if (!chosen.every((v) => v != null)) { event.preventDefault(); return; }
    const combo = byKey.get(key(chosen));
    const addOn = finishOf(finish);
    if (!combo || (combo.sellable === 'live' && combo.variantId && !addOn)) return; // a shelf or live pick adds natively
    event.preventDefault();
    if (combo.sellable === 'staffed') return;
    if (combo.sellable === 'live' && combo.variantId) {
      // With a finish, the ring and its finish go in together as two lines (the form can post only one).
      setButton(m.making, false);
      if (await addLines(combo, addOn)) { window.location.href = cfg.cartUrl; return; }
      status.textContent = m.staffed;
      setButton(m.addToCart, true);
      return;
    }
    setButton(m.making, false);
    status.textContent = m.making;
    const r = await make(combo);
    if (r.down) { status.textContent = m.staffed; setButton(m.addToCart, true); return; }
    if (r.error) { status.textContent = r.error; setButton(m.addToCart, true); return; }
    if (r.kind === 'draft') {
      if (combo.sellable !== 'draft') {
        // The cap was reached between loading and choosing: say so first; the shopper chooses again (C4).
        combo.sellable = 'draft';
        paint();
        if (r.sentence) draftNote.textContent = r.sentence;
        return;
      }
      window.location.href = r.invoiceUrl;
      return;
    }
    // Minted: from now on a live pick. Added through the JSON cart API, not the form: a variant created a moment ago
    // is not yet purchasable on the storefront, and a native post of it lands on the cart silently EMPTY-HANDED
    // (measured 2026-10-02 on Hrh: mint 4.4 s, then the form post added nothing). The API says so (422), so retry briefly.
    combo.variantId = String(r.variantId).replace(/\D/g, '');
    combo.sellable = 'live';
    combo.priceCents = r.priceCents;
    status.textContent = m.adding;
    if (await addLines(combo, addOn)) { window.location.href = cfg.cartUrl; return; }
    status.textContent = m.staffed;
    setButton(m.addToCart, true);
  }

  // The ring, and its finish as a line of its own (D-321): the ring carries the finish code, the add-on names the ring.
  async function addLines(combo, addOn) {
    const ring = { id: Number(combo.variantId), quantity: 1, properties: { _djh_combo: doorKey(combo.options), ...(addOn ? { _djh_finish: addOn.code } : {}) } };
    const items = addOn ? [ring, { id: Number(String(addOn.variantId).replace(/\D/g, '')), quantity: 1, properties: { _djh_finish_for: doorKey(combo.options) } }] : [ring];
    const body = JSON.stringify({ items });
    // A just-minted variant answers 422 ("already sold out") for about 11 s after Shopify creates it, whatever its
    // inventory policy (CATALOG's cartCreate watcher, 2026-10-03: refused through +9.3 s, taken from +10.7 s). A growing
    // backoff overshot that moment by 5.5 s, so ask at a steady 1.25 s; a 429 from the cart's rate limit waits out its
    // Retry-After instead of ending in "call us". Give up only after 40 s.
    const until = Date.now() + 40000;
    const wait = 1250;
    while (Date.now() < until) {
      let pause = wait;
      try {
        const res = await fetch(cfg.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, credentials: 'same-origin', body });
        if (res.ok) return true;
        if (res.status === 429) pause = Math.max(wait, (Number(res.headers.get('Retry-After')) || 2) * 1000);
        else if (res.status !== 422 && res.status !== 404) return false;
      } catch (_e) { /* the network blinked: try again */ }
      await new Promise((r) => setTimeout(r, pause));
    }
    return false;
  }

  (async () => {
    // The fetch theme.liquid started in <head>; asked again once if it failed (the route has been seen answering with an
    // HTML page now and then, and a second ask a moment later gets JSON).
    // With CATALOG's file configured for this store the page reads only the file (the Half Round Band's /feed/offered answer
    // would be 16 MB); without it, the route.
    const ask = async (fresh) => {
      try {
        const cache = fresh ? 'no-cache' : 'default';
        const res = cfg.offeredFile
          ? await fetch(cfg.offeredFile, { headers: { Accept: 'application/json' }, credentials: 'omit', cache })
          : await fetch(`${cfg.offeredUrl}?product=${encodeURIComponent(cfg.productId)}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache });
        return res.ok ? await res.json() : null;
      } catch (_e) {
        return null;
      }
    };
    // The route says {ok:false, reason, retryAfter} (seconds) when the engine is slow or erroring, as a 200, because
    // Shopify's app proxy replaces any 5xx body with the store's page. Then the page keeps "Loading the choices…" and asks
    // again after retryAfter (at most 3 s a wait, ~8 s in all) before showing the dropdown. A failure with no answer at
    // all (network, 5xx) is asked once more at once, as before.
    const usable = (d) => (d && d.ok !== false ? d : null);
    const wait = (ms) => new Promise((r) => { setTimeout(r, ms); });
    const began = performance.now();
    let got = window.djhFeedOffered ? await window.djhFeedOffered : null;
    data = usable(got);
    for (let tries = 0; !data && tries < 4 && performance.now() - began < 8000; tries++) {
      const after = got && got.ok === false ? Number(got.retryAfter) : NaN;
      if (Number.isFinite(after)) await wait(Math.min(Math.max(after, 0.5), 3) * 1000);
      else if (tries > 0) break;
      got = await ask();
      data = usable(got);
    }
    if (data && data.v === 2 && data.grid && Array.isArray(data.grid.p)) data = fromFile(data);
    const loading = $('[data-djh-feed-loading]');
    if (loading) loading.hidden = true;
    if (!data || !Array.isArray(data.axes) || !Array.isArray(data.combos) || !data.combos.length) {
      // The opening ring above stays buyable on its own sizes (pass list 9); the note says the rest is a call away.
      shelf.hidden = false;
      $('[data-djh-feed-down]').hidden = false;
      return;
    }
    squareStones = data.shape === 'square';
    axes = data.axes.map((a) => {
      const metal = a.form ? a.form === 'swatch' : isMetal(a.name);
      const values = a.values.map((v) => v.value);
      values.sort(metal ? (x, y) => metalRank(x) - metalRank(y) : (x, y) => num(x) - num(y));
      // A value made of parts (contract §I.3 "encoded") is picked one part at a time; the Shopify option stays the one
      // value. Stone = Shape · Size (122107) draws shape pictures, then that shape's sizes (split). Any other, e.g. the
      // Half Round Band's Detail · Width · Weight, draws one chip row per part (parted).
      const nParts = Array.isArray(a.parts) ? a.parts.length : 0;
      const partOf = new Map(a.values.filter((v) => Array.isArray(v.parts) && v.parts.length === nParts).map((v) => [v.value, v.parts]));
      const hasParts = !metal && nParts >= 2 && partOf.size === values.length;
      const split = hasParts && nParts === 2 && /shape/i.test(a.parts[0]);
      const parted = hasParts && !split;
      // Each part's values in the order people read them: by number when every one has a number (widths), else the route's.
      const partValues = parted ? a.parts.map((_, j) => {
        const vs = [...new Set(a.values.map((v) => v.parts[j]))];
        return vs.every((x) => Number.isFinite(num(x))) ? vs.sort((x, y) => num(x) - num(y)) : vs;
      }) : null;
      const ctwOf = new Map(a.values.filter((v) => v.ctw && v.ctw.min).map((v) => [v.value, v.ctw]));
      // One value is not a choice: it is drawn as a line (the signet's single stone), never as a row of one-option controls.
      const fixed = !metal && values.length === 1 && !/ring size/i.test(a.name);
      return { name: a.name, metal, fixed, positions: a.positions === 'count' ? 'count' : Number(a.positions) || 1, count: a.form === 'count',
      // CATALOG's canonical key for each Stone value ("Natural Ruby" -> "ruby"): the enquiry carries keys so nobody parses words.
      keyOf: new Map(a.values.filter((v) => v.key).map((v) => [v.value, v.key])), values, split, parted, parts: a.parts, partOf, partValues, partPick: parted ? a.parts.map(() => null) : null, shape: null, ctwOf, hasCtw: split && ctwOf.size > 0 };
    });
    data.combos.forEach((c) => byKey.set(key(c.options), c));
    index();
    // The page opens with a pick already made, as the mockup and Brilliant Earth do, so the carat boxes show at once: the
    // metal and stone of the LOWEST-PRICED ready-made combination, so the price stays at the "From" it loaded with (opening on
    // Shopify's first variant jumped 122107 from $1,435 to $18,195). Never the ring size: that one is the customer's.
    // Shelf first + the card's picture opens the page (Henry, 2026-10-03): the picker opens on the ring the page already
    // showed (cfg.initial: the variant whose photo is the featured photo, the card's), every choice but the ring size;
    // a size tapped on the first-paint chips is kept. Only when that ring isn't in the offered set does it fall back to
    // the lowest-priced ready-made one.
    const opening = document.querySelector('[data-djh-feed-opening]');
    const tappedSize = opening && opening.getAttribute('data-chosen-size');
    const initial = cfg.initial && byKey.get(key(cfg.initial));
    // A URL naming a variant (?variant=, a Google Shopping landing, a shared link) opens on exactly that ring, ring size
    // included, at its own Shopify price, minted ones too (the offered file still calls those 'mint'). "Require a size" is
    // for a plain visit; here the size was chosen by whoever made the link (Integration, 2026-10-03).
    const linked = cfg.linked && initial ? initial : null;
    if (linked) Object.assign(linked, { variantId: String(cfg.linked.id), sellable: 'live', priceCents: cfg.linked.priceCents });
    const opener = linked
      || (initial && initial.sellable === 'live' && initial)
      || data.combos.filter((c) => c.sellable === 'live' && Number.isFinite(c.priceCents)).sort((a, b) => a.priceCents - b.priceCents)[0]
      || { options: cfg.initial || [] };
    const isSize = (a) => /ring size/i.test(a.name);
    const a0 = (axis, want) => (want != null && axis.values.includes(want) ? want : axis.values.includes('4') && axis.count ? '4' : axis.values[0]);
    chosen = axes.map((a, i) => {
      if (isSize(a) && !linked) return opener === initial && tappedSize && a.values.includes(tappedSize) ? tappedSize : null;
      return opener.options[i] != null && a.values.includes(opener.options[i]) ? opener.options[i] : null;
    });
    // A ring whose Stone rows follow its Count axis (71816) opens on the signed first view: the first metal, four stones (or the file's
    // `start`), a mix of birthstones, no size. There is no first-paint ring to match, so the opener above is not used.
    const stoneAt = axes.findIndex((a) => a.positions === 'count');
    const countAt0 = axes.findIndex((a) => a.count);
    if (stoneAt >= 0 && countAt0 >= 0) {
      const st = data.start || {};
      axes.forEach((a, k) => { chosen[k] = null; });
      const mAt = axes.findIndex((a) => a.metal);
      if (mAt >= 0) chosen[mAt] = a0(axes[mAt], st.metal);
      chosen[countAt0] = a0(axes[countAt0], st.count != null ? String(st.count) : '4');
      const stoneValues = axes[stoneAt].values.slice().sort((x, y) => gemRank(x) - gemRank(y));
      startPicks = Array.isArray(st.stones) && st.stones.length === Number(chosen[countAt0])
        ? st.stones.map((v) => stoneValues.find((x) => x === v || gemLabel(x).toLowerCase() === String(v).toLowerCase()) || stoneValues[0])
        : startStones(stoneValues, Number(chosen[countAt0]));
      chosen[stoneAt] = new Set(startPicks).size === 1 ? startPicks[0] : null;
      // Back (or a reload) brings the shopper's metal, count and stones with it: they were kept in the history entry (savePick), not in the address.
      const sv = savedPick();
      if (sv) {
        const okM = mAt < 0 || axes[mAt].values.includes(sv.m);
        const okC = axes[countAt0].values.includes(String(sv.c));
        const okS = Array.isArray(sv.s) && sv.s.length === Number(sv.c) && sv.s.every((v) => stoneValues.includes(v));
        if (okM && okC && okS) {
          openingSeed = key(chosen);
          if (mAt >= 0) chosen[mAt] = sv.m;
          chosen[countAt0] = String(sv.c);
          startPicks = sv.s.slice();
          chosen[stoneAt] = new Set(startPicks).size === 1 ? startPicks[0] : null;
          picked = true;
        }
      }
    }
    touched = true; // the photo follows the opening pick, too
    if (axes[0] && axes[0].metal && chosen[0] == null) chosen[0] = axes[0].values[0];
    axesBox.replaceChildren(...axes.map(drawAxis));
    const finishBox = drawFinish();
    if (finishBox) axesBox.append(finishBox);
    drawSiblings();
    const family = data.family && (data.family.label || (typeof data.family === 'string' ? data.family : ''));
    if (family) {
      const fam = document.querySelector('[data-djh-feed-family]');
      if (fam) { fam.textContent = family; fam.hidden = false; }
    }
    // The picker posts its own id from here on; the first-paint ring's inputs step aside. Disabled is not enough: Shopify's
    // Buy with Shop button reads the FIRST [name="id"] in the form, disabled or not, so those inputs lose their name too, or the
    // button buys the first-paint ring (a 14K Yellow Gold / 4 signet after a White Gold pick, Henry 2026-11-03).
    root.querySelectorAll('[data-djh-feed-opening] [name="id"], [data-djh-feed] > input[name="id"]').forEach((i) => { i.disabled = true; i.removeAttribute('name'); });
    shelf.hidden = true;
    if (opening) opening.hidden = true;
    // The SKU line names the arriving variant until the picker fills it: hidden now, shown per pick by paint().
    if (skuLine) skuLine.hidden = true;
    idInput = el('input', { type: 'hidden', name: 'id', value: '', 'data-djh-feed-id': true });
    comboInput = el('input', { type: 'hidden', name: 'properties[_djh_combo]', value: '', 'data-djh-feed-combo': true });
    picker.append(idInput, comboInput);
    picker.hidden = false;
    if (form) form.addEventListener('submit', onSubmit);
    paint();

    // REFRESH ON RETURN (Henry, 2026-10-03: "this would apply to regular desktop users with stale browsers too"). A page
    // brought back after more than five minutes (a tab returned to, or Back from the cart, which restores the page from
    // the browser's cache with its old prices) asks for the offered set again and repaints. A price that changed shows
    // the new one; a pick no longer offered stops selling. The shopper's choices stay.
    let loadedAt = Date.now();
    let refreshing = false;
    const refresh = async () => {
      if (refreshing || Date.now() - loadedAt < 5 * 60 * 1000) return;
      refreshing = true;
      try {
        let fresh = usable(await ask(true));
        if (fresh && fresh.v === 2 && fresh.grid && Array.isArray(fresh.grid.p)) fresh = fromFile(fresh);
        if (!fresh || !Array.isArray(fresh.combos) || !fresh.combos.length) return;
        const was = byKey;
        byKey = new Map();
        fresh.combos.forEach((c) => {
          const old = was.get(key(c.options));
          // What a pick learned since load (its SKU and facts, a variant minted or linked) is kept; price and state are new.
          if (old) {
            ['sku', 'facts', 'imageUrl', 'images', 'finishCodes', 'finishImages'].forEach((f) => { if (c[f] == null && old[f] != null) c[f] = old[f]; });
            if (!c.variantId && old.variantId) { c.variantId = old.variantId; if (c.sellable === 'mint') c.sellable = 'live'; }
            if (old.needsDetail === false) c.needsDetail = false;
          }
          byKey.set(key(c.options), c);
        });
        data.combos = fresh.combos;
        index();
        loadedAt = Date.now();
        paint();
      } finally {
        refreshing = false;
      }
    };
    // A page restored from the back/forward cache can bring its canvas back blank: the composed picture is drawn again from the layers in memory.
    window.addEventListener('pageshow', (e) => { if (e.persisted && cmp.cv) { cmp.shown = null; cmp.wantKey = null; paint(); } });
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refresh(); });
  })();
})();
